import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth, storage, handleFirestoreError, OperationType, generateNextApplicationId, uploadBytesWithTimeout, getDownloadURL, getStorageErrorMessage, resolveFirebaseDocumentUrl, getUploadedStoragePath, createAuditLog } from '../../../lib/firebase';
import { onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import MasterPipeline, { MASTER_STAGES } from '../../MasterPipeline';
import ActivityTimeline from '../../ActivityTimeline';
import type { ActivityEntry } from '../../ActivityTimeline';
import AdvancedFilters from '../../AdvancedFilters';
import {
  collection, doc, query, where, orderBy, deleteDoc, limit,
  writeBatch,
  getDocs as plainGetDocs, getDoc as plainGetDoc
} from 'firebase/firestore';
// Plain getters are used ONLY for reads that are not the application
// document itself (auditLogs, documentRequests, and the documents /
// specialRequests subcollections). Those records carry no tiered PII
// fields, and routing them through secureApplicationsRepo POSTs them to
// /api/pii/decrypt anchored on a stored applications/{id} record — a
// subcollection/other-collection id has no such anchor, so the endpoint
// refuses every read with HTTP 403 (same defect class as the
// 2026-10-26 template-variable registry outage, fixed in PR #1395).
// Reads of the application document itself keep the secure repo.
import {
  secureGetDocs as getDocs,
  secureGetDoc as getDoc,
  secureUpdateDoc as updateDoc,
  secureSetDoc as setDoc,
  secureAddDoc as addDoc
} from '../../../lib/secureApplicationsRepo';
import { isAcceptableDocumentFileName, unsupportedTypeMessage } from '../../../lib/documentFileTypes';
import { applyDocumentDecision } from '../../../lib/documentReviewGuard';
import { ref, deleteObject } from 'firebase/storage';
// @ts-ignore
import imageCompression from 'browser-image-compression';
import { Application, ApplicationStatus, ServiceProvider, DocumentMetadata, Comment, AuditLog, SpecialRequest, Program, DocumentRequest } from '../../../types';
import { usePath, navigate, parseRoute } from '../../../lib/router';
import { normalizeApplicationForDisplay, normalizePassportDates } from '../../../lib/applicationSchema';
import { firstUsablePersonName, sanitizeNamePart } from '../../../lib/nameUtils';
import { ApplicantFilterState, DEFAULT_FILTER_STATE, filterApplicantRecord } from '../../../lib/applicantFilterUtils';
import { confirmNotification, sendSystemNotification, buildEmailTemplate } from "../../../lib/notifications";
import { confirmDialog } from '../../../lib/confirmStore';
import type { AdminTabId } from '../../../lib/adminTabs';

/**
 * ADM-13 (decision D5): where a cross-surface "view applicant" jump came
 * from. `viewApplicantInWorkspace` records this so the Applicants
 * workspace can offer a return path; AdminDashboard consumes it once
 * (Back control, or cleared when the admin navigates away normally).
 * `subView` covers origins inside the applicants tab itself (the
 * archived list), where returning means restoring that sub-view and
 * deselecting rather than switching tabs.
 */
export interface ApplicantReturnOrigin {
  tab: AdminTabId;
  subView?: 'archived';
}
import { notifyToast } from '../../../lib/toastStore';
import { safeFetch, triggerDocumentExtraction } from '../../../lib/api';
import { getActiveWorkspaceId, workspaceHeader } from '../../../lib/workspaces';
import { useWorkspaces } from '../../shared/WorkspaceProvider';
import { isApplicationActive } from '../../../lib/constants';
import { getPlacementSummary } from '../../../lib/placementUtils';
import { normalisePricingVariables } from '../../../lib/programConfig';
// PT7-003: Load providers from Firestore serviceProviders collection instead of
// shipping hardcoded PII (names, emails, phones) in the JS bundle.

const compressImage = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) return file;
  try {
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type,
    });
  } catch (err) {
    console.warn('Image compression failed, using original:', err);
    return file;
  }
};

// In-memory cache for admin applications keyed by user and workspace (zero disk footprint)
let inMemoryAppsCache: Application[] | null = null;
let inMemoryAppsCacheAt: number = 0;
let inMemoryAppsCacheKey: string = '';

const clearApplicationsCache = () => {
  inMemoryAppsCache = null;
  inMemoryAppsCacheAt = 0;
  inMemoryAppsCacheKey = '';
  try {
    localStorage.removeItem('all_cached_applications');
    localStorage.removeItem('all_cached_applications_at');
    localStorage.removeItem('all_cached_applications_ver');
  } catch (_) {}
};

export function useApplications(adminEmail: string, options?: { enabled?: boolean }) {
  // Follow-up Item 12: the bootstrap fetch below keys its cache and its
  // workspace header off the workspace id stored at call time. If
  // WorkspaceProvider resolves/validates that id AFTER bootstrap, the
  // list must refetch in the resolved context — otherwise it serves the
  // wrong-workspace result until a manual refresh.
  const { loading: workspaceLoading, activeWorkspaceId } = useWorkspaces();
  const bootstrappedWorkspaceRef = useRef<string | null>(null);
  // When `enabled` is false the hook never loads the (large) applicant dataset,
  // so members without access to any applicant-backed module do not have that
  // data fetched into client state. Permission filtering happens before load.
  const bootstrapEnabled = options?.enabled !== false;

  // state + handlers
  const [applications, setApplications] = useState<Application[]>([]);
  const applicationsRef = useRef<Application[]>([]);
  useEffect(() => {
    applicationsRef.current = applications;
  }, [applications]);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [masterStage, setMasterStage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedArchivedIds, setSelectedArchivedIds] = useState<Set<string>>(new Set());
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Auth gate: all Firestore reads are blocked until this is true.
  // onAuthStateChanged sets it once the Firebase session is confirmed,
  // ensuring request.auth is never null when our reads hit Firestore rules.
  const [authReady, setAuthReady] = useState<boolean>(false);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync selectedApp with URL path
  useEffect(() => {
    if (!authReady) return;
    if (currentRoute.type === 'applications_detail') {
      const targetId = currentRoute.id;
      if (selectedApp?.applicationId !== targetId) {
        const found = applications.find(a => a.applicationId === targetId);
        if (found) {
          handleSelectApplicant(found);
        } else if (!loading && applications.length > 0) {
          const placeholder: any = { applicationId: targetId };
          handleSelectApplicant(placeholder);
        } else if (applications.length === 0) {
          const placeholder: any = { applicationId: targetId };
          handleSelectApplicant(placeholder);
        }
      }
    } else {
      if (selectedApp && (currentPath === '/applications' || currentPath === '/dashboard')) {
        setSelectedApp(null);
      }
    }
  }, [currentPath, applications, loading, authReady]);

  // Analytics
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  // Reset to page 1 when page size changes
  React.useEffect(() => { setCurrentPage(1); }, [itemsPerPage]);
  // ADM-13 (decision D5): the preview modal is gone — there is exactly
  // one editable applicant workspace. Cross-surface "view applicant"
  // actions go through viewApplicantInWorkspace (below), which selects
  // the applicant in the Applicants split view and records the origin
  // for the return path.
  const [applicantReturnOrigin, setApplicantReturnOrigin] = useState<ApplicantReturnOrigin | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    submitted: 0,
    returned: 0,
    cancelled: 0,
  });

  // Filter & Search states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [workflowPhaseFilter, setWorkflowPhaseFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [programFilter, setProgramFilter] = useState<string[]>([]);
  const [pricingVariableFilter, setPricingVariableFilter] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [masterStageFilter, setMasterStageFilter] = useState<string[]>([]);
  const [financialFilter, setFinancialFilter] = useState<string[]>([]);
  const [genderFilter, setGenderFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [partnerFilter, setPartnerFilter] = useState<string[]>([]);
  const [batchFilter, setBatchFilter] = useState<string[]>([]);
  const [termFilter, setTermFilter] = useState<string[]>([]);
  const [referrerFilter, setReferrerFilter] = useState<string[]>([]);
  const [agentFilter, setAgentFilter] = useState<string[]>([]);
  const [placementFilter, setPlacementFilter] = useState<string[]>([]);
  const [submittedDateFrom, setSubmittedDateFrom] = useState<string>('');
  const [submittedDateTo, setSubmittedDateTo] = useState<string>('');
  const [applicantViewMode, setApplicantViewMode] = useState<'table' | 'card'>('table');
  const [advFilters, setAdvFilters] = useState({ country: '', gender: '', partner: '', dateFrom: '', dateTo: '' });
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);

  const unifiedFilters: ApplicantFilterState = React.useMemo(() => ({
    workflowPhase: workflowPhaseFilter,
    sponsor: partnerFilter,
    gender: genderFilter,
    country: countryFilter,
    financialStation: financialFilter,
    status: statusFilter,
    masterPhase: masterStageFilter,
    tags: tagFilter,
    placementStatus: placementFilter,
    createdFrom: advFilters.dateFrom || '',
    createdTo: advFilters.dateTo || '',
    submittedFrom: submittedDateFrom,
    submittedTo: submittedDateTo,
    program: programFilter,
    pricingVariable: pricingVariableFilter,
    term: termFilter,
    batches: batchFilter,
    referrer: referrerFilter,
    assignedTeamMember: agentFilter,
  }), [workflowPhaseFilter, partnerFilter, genderFilter, countryFilter, financialFilter, statusFilter, masterStageFilter, tagFilter, placementFilter, advFilters.dateFrom, advFilters.dateTo, submittedDateFrom, submittedDateTo, programFilter, termFilter, batchFilter, referrerFilter, agentFilter]);

  const setUnifiedFilters = (next: ApplicantFilterState) => {
    setWorkflowPhaseFilter(next.workflowPhase || []);
    setPartnerFilter(next.sponsor || []);
    setGenderFilter(next.gender || []);
    setCountryFilter(next.country || []);
    setFinancialFilter(next.financialStation || []);
    setStatusFilter(next.status || []);
    setMasterStageFilter(next.masterPhase || []);
    setTagFilter(next.tags || []);
    setPlacementFilter(next.placementStatus || []);
    setAdvFilters(prev => ({ ...prev, dateFrom: next.createdFrom || '', dateTo: next.createdTo || '' }));
    setSubmittedDateFrom(next.submittedFrom || '');
    setSubmittedDateTo(next.submittedTo || '');
    setProgramFilter(next.program || []);
    setPricingVariableFilter(next.pricingVariable || []);
    setTermFilter(next.term || []);
    setBatchFilter(next.batches || []);
    setReferrerFilter(next.referrer || []);
    setAgentFilter(next.assignedTeamMember || []);
    setCurrentPage(1);
  };
  const [editMode, setEditMode] = useState<'off' | 'personal' | 'academic' | 'preferences'>('off');
  const [editForm, setEditForm] = useState<any>({});
  
  // Detail page sub-states
  const [docsList, setDocsList] = useState<DocumentMetadata[]>([]);
  const [specialRequests, setSpecialRequests] = useState<SpecialRequest[]>([]);
  const [documentRequests, setDocumentRequests] = useState<DocumentRequest[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  
  // Input fields for Admin forms
  const [newComment, setNewComment] = useState<string>('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState<string>('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyText, setEditingReplyText] = useState<string>('');
  const [newRequestName, setNewRequestName] = useState<string>('');
  const [newRequestDesc, setNewRequestDesc] = useState<string>('');
  const [newRequestDueDate, setNewRequestDueDate] = useState<string>('');
  const [newReqDocumentType, setNewReqDocumentType] = useState<string>('Required');
  const [newReqDocPriority, setNewReqDocPriority] = useState<string>('Medium');
  const [newRequestResponseType, setNewRequestResponseType] = useState<string>('short_text');
  const [newRequestInstructions, setNewRequestInstructions] = useState<string>('');
  const [newRequestRequiredDocs, setNewRequestRequiredDocs] = useState<string>('');
  const [newRequestDropdownOptions, setNewRequestDropdownOptions] = useState<string>('');
  const [newRequestTextResponseType, setNewRequestTextResponseType] = useState<string>('short_text');
  const [newRequestFormFields, setNewRequestFormFields] = useState<any[]>([]);

  // Input states for editing a document request
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [editingRequestName, setEditingRequestName] = useState<string>('');
  const [editingRequestType, setEditingRequestType] = useState<string>('Required');
  const [editingRequestDesc, setEditingRequestDesc] = useState<string>('');
  const [editingRequestDueDate, setEditingRequestDueDate] = useState<string>('');
  const [editingRequestPriority, setEditingRequestPriority] = useState<string>('Medium');
  const [editingRequestResponseType, setEditingRequestResponseType] = useState<string>('short_text');
  const [editingRequestInstructions, setEditingRequestInstructions] = useState<string>('');
  const [editingRequestRequiredDocs, setEditingRequestRequiredDocs] = useState<string>('');
  const [editingRequestDropdownOptions, setEditingRequestDropdownOptions] = useState<string>('');
  const [editingRequestTextResponseType, setEditingRequestTextResponseType] = useState<string>('short_text');
  const [editingRequestFormFields, setEditingRequestFormFields] = useState<any[]>([]);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>('');
  const [adminListViewMode, setAdminListViewMode] = useState<'card' | 'list'>('list');
  // The tab type is the canonical AdminTabId (lib/adminTabs, ADM-07) —
  // this union used to be hand-declared here and silently lost
  // 'documents', 'automations', 'workflow' and 'finance'.
  const [adminTab, setAdminTab] = useState<AdminTabId>(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const queryTab = searchParams.get('tab');
      // ADM-08 (decision D4): 'senders' is retired as a standalone tab —
      // Email Senders' one home is the Workspace Settings sub-tab. Old
      // ?tab=senders deep links land on Settings (the settingsInitialSubTab
      // hint below selects the Email Senders sub-tab there).
      if (queryTab === 'senders') return 'settings';
      // URL-derived values keep their accept-verbatim contract (the
      // render chain falls back for unknown ids) but are asserted
      // against the canonical union — never `as any`.
      if (queryTab) return queryTab as AdminTabId;

      const p = window.location.pathname;
      const r = parseRoute(p);
      if (r.type === 'applications_detail') return 'applicants';
      if (r.type === 'events_detail') return 'my-events';
      if (r.type === 'sponsors_detail') return 'sponsors';
      if (r.type === 'employers_detail') return 'employers';
      if (r.type === 'referrers_detail') return 'referrers';
      if (r.type === 'programs_detail') return 'settings';
      if (r.type === 'organizations_detail') return 'organizations';
      if (r.type === 'users_detail') return 'users';
      if (r.type === 'tickets_detail') return 'tickets';
      if (r.type === 'base') {
        const norm = r.section.toLowerCase();
        if (norm === 'applications' || norm === 'applicants') return 'applicants';
        if (norm === 'events') return 'events';
        if (norm === 'myevents' || norm === 'my-events') return 'my-events';
        // ADM-08: the retired /senders path lands on Settings too.
        if (norm === 'senders') return 'settings';
        return norm as AdminTabId;
      }
    }
    return 'dashboard';
  });
  // ADM-08 (decision D4): one-shot hint for AdminSettings' initial
  // sub-tab, mirroring AdminSettings' sub-tab union. A boot URL of
  // ?tab=senders (the retired standalone tab) selects the Email Senders
  // sub-tab; AdminDashboard clears the hint back to 'programs' once the
  // admin leaves Settings, so later Settings visits open on the default.
  const [settingsInitialSubTab, setSettingsInitialSubTab] = useState<'programs' | 'providers' | 'senders'>(() => {
    if (typeof window !== 'undefined') {
      if (new URLSearchParams(window.location.search).get('tab') === 'senders') return 'senders';
    }
    return 'programs';
  });
  // ADM-13 (decision D5): the single entry point for cross-surface
  // applicant viewing — select the applicant in the one workspace,
  // record where the jump came from, and switch to the Applicants tab.
  const viewApplicantInWorkspace = useCallback((app: Application, origin: ApplicantReturnOrigin) => {
    setSelectedApp(app);
    setApplicantReturnOrigin(origin);
    setAdminTab('applicants');
  }, []);
  const [detailPrimaryTab, setDetailPrimaryTab] = useState<'personal' | 'academic' | 'preferences' | 'events'>('personal');
  const [detailSecondaryTab, setDetailSecondaryTab] = useState<'assignments' | 'documents' | 'notebook'>('assignments');

  // Document and comment threading additions
  const [commentFile, setCommentFile] = useState<File | null>(null);
  const [isUploadingCommentFile, setIsUploadingCommentFile] = useState(false);
  const [commentFileUrl, setCommentFileUrl] = useState<string | null>(null);
  const [commentFileName, setCommentFileName] = useState<string | null>(null);
  const [commentFileProgress, setCommentFileProgress] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentMetadata | null>(null);
  const [replyInputs, setReplyInputs] = useState<Record<string, string>>({});
  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [programsList, setProgramsList] = useState<Program[]>([]);

  // Editing state for document titles
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingDocName, setEditingDocName] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [rejectionModalDocId, setRejectionModalDocId] = useState<string | null>(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState<string>('');

  // ─── Caches & Optimization ──────────────────────────────────────────────
  // URL cache: resolves Firebase Storage paths to download URLs with a 45-min TTL
  const urlCache = useRef<Map<string, { url: string; expiresAt: number }>>(new Map());

  // Tab-loaded tracking: only fetch subcollections once per tab
  const loadedTabs = useRef<Set<string>>(new Set());
  // ──────────────────────────────────────────────────────────────────────────

  // Helper: resolve storage path to download URL with TTL caching
  // (Not a hook — called only from event handlers, not during render)
  const getCachedUrl = async (storagePath: string, downloadUrl?: string): Promise<string> => {
    if (downloadUrl?.startsWith('http')) return downloadUrl;
    const cached = urlCache.current.get(storagePath);
    if (cached && Date.now() < cached.expiresAt) return cached.url;
    const url = await resolveFirebaseDocumentUrl(storagePath, downloadUrl || null);
    urlCache.current.set(storagePath, { url: url || storagePath, expiresAt: Date.now() + 45 * 60 * 1000 });
    return url || storagePath;
  };

  // --- Dynamic Administrative Edit Panel States ---
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [editPersonal, setEditPersonal] = useState<any>(null);

  // --- Quick Candidate Creation ---
  const [isCreatingCandidate, setIsCreatingCandidate] = useState(false);
  const [candidateFirstName, setCandidateFirstName] = useState('');
  const [candidateLastName, setCandidateLastName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateProgram, setCandidateProgram] = useState('');
  const [candidateProgramTerm, setCandidateProgramTerm] = useState('');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [resumeParsed, setResumeParsed] = useState(false);
  const [candidateRequirementsConfirmed, setCandidateRequirementsConfirmed] = useState(false);
  const [candidateResumeUploaded, setCandidateResumeUploaded] = useState(false);
  const [candidateResumeParsed, setCandidateResumeParsed] = useState(false);

  // --- Assignment Panel Editing States ---
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [editAssignmentEmployer, setEditAssignmentEmployer] = useState('');
  const [editAssignmentRole, setEditAssignmentRole] = useState('');
  const [editAssignmentCity, setEditAssignmentCity] = useState('');
  const [editAssignmentState, setEditAssignmentState] = useState('');
  const [editAssignmentCountry, setEditAssignmentCountry] = useState('United States');

  // --- Visa Panel Editing States ---
  const [isEditingVisa, setIsEditingVisa] = useState(false);
  const [editVisaNo, setEditVisaNo] = useState('');
  const [editVisaStatus, setEditVisaStatus] = useState('Pending');
  const [editVisaDate, setEditVisaDate] = useState('');
  const [editVisaLocation, setEditVisaLocation] = useState('');

  // --- Embassy Appointment Editing States ---
  const [isEditingEmbassy, setIsEditingEmbassy] = useState(false);
  const [editEmbassyDate, setEditEmbassyDate] = useState('');
  const [editEmbassyLocation, setEditEmbassyLocation] = useState('');
  const [editEmbassyResult, setEditEmbassyResult] = useState('Pending');

  // --- DS-160 Editing States ---
  const [isEditingDs160, setIsEditingDs160] = useState(false);
  const [editDs160No, setEditDs160No] = useState('');
  const [editDs160QuestionAnswer, setEditDs160QuestionAnswer] = useState('');
  const [editDs160Status, setEditDs160Status] = useState('Pending');

  // --- Flight Panel Editing States ---
  const [isEditingFlight, setIsEditingFlight] = useState(false);
  const [editFlightAirline, setEditFlightAirline] = useState('');
  const [editFlightNo, setEditFlightNo] = useState('');
  const [editFlightDepDateTime, setEditFlightDepDateTime] = useState('');
  const [editFlightArrDateTime, setEditFlightArrDateTime] = useState('');
  const [editFlightDepAirport, setEditFlightDepAirport] = useState('');
  const [editFlightArrAirport, setEditFlightArrAirport] = useState('');
  const [editFlightNotes, setEditFlightNotes] = useState('');

  const [isEditingPassport, setIsEditingPassport] = useState(false);
  const [editPassport, setEditPassport] = useState<any>(null);
  const [editPassportNo, setEditPassportNo] = useState('');
  const [editPassportCountry, setEditPassportCountry] = useState('');
  const [editPassportIssue, setEditPassportIssue] = useState('');
  const [editPassportExpiry, setEditPassportExpiry] = useState('');
  const [editPassVisaStatus, setEditPassVisaStatus] = useState('');
  const [editDs2019, setEditDs2019] = useState('');
  const [editSevisId, setEditSevisId] = useState('');
  const [editIntendedArrival, setEditIntendedArrival] = useState('');
  const [editUsDestination, setEditUsDestination] = useState('');

  const [isEditingEducation, setIsEditingEducation] = useState(false);
  const [editEducation, setEditEducation] = useState<any[]>([]);

  const [isEditingWork, setIsEditingWork] = useState(false);
  const [editWork, setEditWork] = useState<any[]>([]);

  const [isEditingEmergency, setIsEditingEmergency] = useState(false);
  const [editEmergency, setEditEmergency] = useState<any[]>([]);

  const [isEditingProgram, setIsEditingProgram] = useState(false);
  const [editProgram, setEditProgram] = useState<any>(null);
  const [editPricingVariableId, setEditPricingVariableId] = useState('');
  const [editProgramTerm, setEditProgramTerm] = useState('');
  const [editProgIndustry, setEditProgIndustry] = useState('');
  const [editProgState, setEditProgState] = useState('');
  const [editProgStart, setEditProgStart] = useState('');
  const [editProgDuration, setEditProgDuration] = useState('');
  const [editProgEnglish, setEditProgEnglish] = useState('');
  const [editProgSkills, setEditProgSkills] = useState('');
  const [editProgNotes, setEditProgNotes] = useState('');
  const [editProgRelocate, setEditProgRelocate] = useState(true);

  const [adminNoteInput, setAdminNoteInput] = useState<string>('');

  const [isGeneratingProfileModalOpen, setIsGeneratingProfileModalOpen] = useState(false);

  // On-behalf document upload state
  const [adminDocType, setAdminDocType] = useState<string>('Resume/CV');
  const [adminUploadedFile, setAdminUploadedFile] = useState<File | null>(null);
  const [adminUploading, setAdminUploading] = useState(false);
  const [adminUploadProgress, setAdminUploadProgress] = useState<number | null>(null);

  // Global service providers loaded
  const [providers, setProviders] = useState<ServiceProvider[]>([]);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);
  const [adminDetailTab, setAdminDetailTab] = useState<'profile' | 'placement' | 'documents' | 'activity' | 'events' | 'finance' | 'sign' | 'tickets'>('profile');

  // ─── AUTH GATE ────────────────────────────────────────────────────────────
  // Wait for Firebase Auth to confirm the session before running ANY Firestore
  // reads. Without this gate, reads fire while request.auth is still null and
  // Firestore security rules reject every request with "permission-denied".
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // P0-1a: no client-side PII keys exist anymore (server-side
      // transforms), so the auth gate no longer waits on key fetch.
      if (user) {
        setAuthReady(true);
      } else {
        setAuthReady(false);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // ─── DATA BOOTSTRAP ──────────────────────────────────────────────────────
  // Only runs once authReady flips to true, guaranteeing a valid token exists.
  useEffect(() => {
    if (!authReady || workspaceLoading) return;
    if (bootstrapEnabled) {
      if (bootstrappedWorkspaceRef.current !== null && bootstrappedWorkspaceRef.current !== activeWorkspaceId) {
        // Workspace context changed after bootstrap — the cached list is
        // from the wrong context; force a fresh fetch.
        fetchApplications({ force: true });
      } else {
        fetchApplications();
      }
      bootstrappedWorkspaceRef.current = activeWorkspaceId;
      // auditLogs are lazy-loaded on tab switch — removed from bootstrap
    } else {
      // Member has no applicant-backed module — release the loading state so
      // the dashboard skeleton does not spin forever.
      setLoading(false);
    }
    // Providers & programs are lightweight and consumed across many pages.
    fetchProviders();
    fetchAndSeedPrograms();
  }, [authReady, bootstrapEnabled, workspaceLoading, activeWorkspaceId]);


  const fetchAndSeedPrograms = async () => {
    try {
      const data = await safeFetch('/api/admin/programs');
      const list: Program[] = (data.programs || []).map((p: any) => ({
        id: p.id || p.programId,
        programId: p.programId || p.id,
        name: p.name,
        code: p.code,
        description: p.description || '',
        requiredDocuments: p.requiredDocuments || [],
        active: p.active ?? p.isOpen ?? true,
        terms: p.terms || [],
        requirements: p.requirements || '',
        documentGuide: p.documentGuide || '',
        pricingVariables: normalisePricingVariables(p.pricingVariables),
      } as Program));
      setProgramsList(list);
      sessionStorage.setItem('programsList', JSON.stringify(list));
      sessionStorage.setItem('programsList_at', String(Date.now()));
      sessionStorage.setItem('programsList_ver', '2');
    } catch (err) {
      console.warn("Could not fetch programs list: ", err);
    }
  };

  const fetchProviders = async () => {
    try {
      const data = await safeFetch('/api/admin/service-providers');
      const list: ServiceProvider[] = (data.providers || []).map((p: any) => ({
        providerId: p.id || p.providerId,
        name: p.name || '',
        contactName: p.contactName || '',
        contactEmail: p.contactEmail || '',
        contactPhone: p.contactPhone || '',
        companyName: p.companyName || p.name || '',
      } as ServiceProvider));
      if (list.length > 0) setProviders(list);
    } catch (e) {
      console.warn('Could not fetch providers:', e);
    }
  };

  const fetchApplications = async (opts?: { force?: boolean }) => {
    const currentUid = auth.currentUser?.uid || '';
    const currentWs = getActiveWorkspaceId() || '';
    const currentKey = `${currentUid}:${currentWs}`;

    const CACHE_TTL = 30 * 1000;
    if (!opts?.force && inMemoryAppsCache && inMemoryAppsCacheKey === currentKey && Date.now() - inMemoryAppsCacheAt < CACHE_TTL) {
      setApplications(inMemoryAppsCache);
      calculateStats(inMemoryAppsCache);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let apps: Application[] = [];
      const data = await safeFetch('/api/admin/applicant-profiles?limit=2000');
      // P0-1a: /api/admin/applicant-profiles decrypts server-side for
      // callers cleared to see contact PII — no client transform.
      apps = (data.applications || []).map((app: any) => (
        normalizeApplicationForDisplay(app as Application) as Application
      ));

      setApplications(apps);
      calculateStats(apps);

      // Cache in memory keyed by identity and workspace
      inMemoryAppsCache = apps;
      inMemoryAppsCacheAt = Date.now();
      inMemoryAppsCacheKey = currentKey;
      // Backwards compatibility: clear any old disk-persisted caches
      try {
        localStorage.removeItem('all_cached_applications');
        localStorage.removeItem('all_cached_applications_at');
        localStorage.removeItem('all_cached_applications_ver');
      } catch (_) {}
    } catch (err) {
      console.error("Firestore read error on admin portal:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (apps: Application[]) => {
    const activeApps = (apps || []).filter(a => isApplicationActive(a));
    const getSubmissionStatus = (a: Application) =>
      a ? (a as any).submissionStatus || a.status || 'Draft' : 'Draft';
    const s = {
      total: activeApps.length,
      inProgress: activeApps.filter(a => getSubmissionStatus(a) === 'Draft').length,
      submitted: activeApps.filter(a => getSubmissionStatus(a) === 'Submitted').length,
      returned: activeApps.filter(a => getSubmissionStatus(a) === 'Returned').length,
      cancelled: apps.filter(a => !isApplicationActive(a)).length,
    };
    setStats(s);
  };

  const fetchAuditLogs = async (applicantId?: string) => {
    if (!auth.currentUser || !authReady) return;
    try {
      if (applicantId) {
        // Scoped to a single applicant — used when Activity tab opens
        const q = query(
          collection(db, 'auditLogs'),
          where('targetId', '==', applicantId),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        const snap = await plainGetDocs(q);
        const logs: AuditLog[] = [];
        snap.forEach(docSnap => {
          logs.push({ logId: docSnap.id, ...docSnap.data() } as AuditLog);
        });
        setAuditLogs(logs);
      } else {
        // Global fetch — only used when no applicant is selected (dashboard-wide view)
        const q = query(collection(db, 'auditLogs'), orderBy('timestamp', 'desc'), limit(500));
        const snap = await plainGetDocs(q);
        const logs: AuditLog[] = [];
        snap.forEach(docSnap => {
          logs.push({ logId: docSnap.id, ...docSnap.data() } as AuditLog);
        });
        logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setAuditLogs(logs);
      }
    } catch (err) {
      console.warn("Could not fetch remote auditLogs:", err);
    }
  };



  // PT7-001: Now using shared createAuditLog from firebase.ts (server-side write)

  // Open applicant detail page
  const handleSelectApplicant = async (app: Application) => {
    // Sync the URL path to match the selection
    navigate(`/applications/adminview/${app.applicationId}`);

    // Capture the current detail tab BEFORE resetting state, so we can keep it active
    const currentTab = adminDetailTab;

    // Instantly use available list data to render detail shell without waiting for network
    const initialApp = normalizeApplicationForDisplay(app) as Application;

    setSelectedApp(initialApp);
    setMasterStage((initialApp as any).masterStage ?? 0);
    setLoading(true);

    // Helper to populate edit form state from an app record
    const populateFormFields = (data: Application) => {
      setNewComment('');
      setNewRequestName('');
      setNewRequestDesc('');
      setNewRequestDueDate('');
      setProfilePhotoUrl(data.profilePhotoUrl || data.personalDetails?.fullNameLegal || '');

      setIsEditingPersonal(false);
      setEditPersonal(null);
      setIsEditingPassport(false);
      setEditPassport(null);
      const passportData = normalizePassportDates(data.passportImmigration || {});
      setEditPassportNo(passportData.passportNumber || '');
      setEditPassportCountry(passportData.passportCountry || '');
      setEditPassportIssue(passportData.issueDate || '');
      setEditPassportExpiry(passportData.expirationDate || '');
      setEditPassVisaStatus(passportData.visaStatus || '');
      setEditDs2019(passportData.ds2019Number || '');
      setEditSevisId(passportData.sevisId || '');
      setEditIntendedArrival(passportData.intendedArrival || '');
      setEditUsDestination(passportData.usDestination || '');
      setIsEditingEducation(false);
      setEditEducation([]);
      setIsEditingWork(false);
      setEditWork([]);
      setIsEditingEmergency(false);
      setEditEmergency([]);
      setIsEditingProgram(false);
      setEditProgram(null);
      setEditProgramTerm('');
      setEditProgIndustry(data.programPreferences?.preferredIndustry || '');
      setEditProgState(data.programPreferences?.preferredState || '');
      setEditProgStart(data.programPreferences?.startDateAvailability || '');
      setEditProgDuration(data.programPreferences?.programDuration?.toString() || '');
      setEditProgEnglish(data.programPreferences?.englishProficiency || '');
      setEditProgSkills(data.programPreferences?.specialSkills || '');
      setEditProgNotes(data.programPreferences?.additionalNotes || '');
      setEditProgRelocate(data.programPreferences?.willingnessToRelocate ?? true);
      setAdminNoteInput(data.adminNotes || '');

      setEditAssignmentEmployer(data.assignment?.employerName || '');
      setEditAssignmentRole(data.assignment?.roleTitle || '');
      setEditAssignmentCity(data.assignment?.city || '');
      setEditAssignmentState(data.assignment?.state || '');
      setEditAssignmentCountry(data.assignment?.country || 'United States');
      setIsEditingAssignment(false);

      setEditVisaNo(data.visaDetails?.visaApplicationNumber || '');
      setEditVisaStatus(data.visaDetails?.visaApprovalStatus || 'Pending');
      setEditVisaDate(data.visaDetails?.embassyAppointmentDate || '');
      setEditVisaLocation(data.visaDetails?.embassyLocation || '');
      setIsEditingVisa(false);

      setEditFlightAirline(data.flightDetails?.airline || '');
      setEditFlightNo(data.flightDetails?.flightNumber || '');
      setEditFlightDepDateTime(data.flightDetails?.departureDateTime || '');
      setEditFlightArrDateTime(data.flightDetails?.arrivalDateTime || '');
      setEditFlightDepAirport(data.flightDetails?.departureAirport || '');
      setEditFlightArrAirport(data.flightDetails?.arrivalAirport || '');
      setEditFlightNotes(data.flightDetails?.additionalNotes || '');
      setIsEditingFlight(false);
    };

    populateFormFields(initialApp);

    // Reset tab-loaded tracking and cleared sub-lists when switching applicants
    loadedTabs.current.clear();
    setComments([]);
    setDocsList([]);

    // Execute background queries in PARALLEL using Promise.all
    try {
      const freshDocPromise = getDoc(doc(db, 'applications', app.applicationId)).catch(e => {
        console.warn('Failed to fetch fresh applicant data, using cached version:', e);
        return null;
      });

      const specialReqsPromise = plainGetDocs(collection(db, 'applications', app.applicationId, 'specialRequests')).catch(dbErr => {
        console.warn('Firestore specialRequests read failed:', dbErr);
        return null;
      });

      const docReqsPromise = plainGetDocs(query(collection(db, 'documentRequests'), where('applicantId', '==', app.applicationId))).catch(reqsErr => {
        console.warn('documentRequests firebase fetch failed:', reqsErr);
        return null;
      });

      const [freshSnap, reqsSnap, docReqsSnap] = await Promise.all([
        freshDocPromise,
        specialReqsPromise,
        docReqsPromise
      ]);

      let freshApp = initialApp;
      if (freshSnap && freshSnap.exists()) {
        freshApp = normalizeApplicationForDisplay({ ...freshSnap.data(), applicationId: freshSnap.id }) as Application;
        setSelectedApp(freshApp);
        setMasterStage((freshApp as any).masterStage ?? 0);
        populateFormFields(freshApp);
      }

      if (reqsSnap) {
        const reqs: SpecialRequest[] = [];
        reqsSnap.forEach(snap => {
          reqs.push({ requestId: snap.id, ...snap.data() } as SpecialRequest);
        });
        setSpecialRequests(reqs);
      }

      if (docReqsSnap) {
        const reqsList: DocumentRequest[] = [];
        docReqsSnap.forEach(snap => {
          reqsList.push({ requestId: snap.id, ...snap.data() } as DocumentRequest);
        });
        setDocumentRequests(reqsList);
      } else {
        setDocumentRequests([]);
      }

      setLoading(false);

      if (currentTab !== 'profile') {
        handleLazyLoadTab(currentTab, freshApp);
      }
    } catch (err) {
      console.error("handleSelectApplicant error:", err);
      setLoading(false);
    }
  };

  // ─── Tab-based lazy loading ──────────────────────────────────────────────
  // Called from ApplicantDetail when the user switches between tabs.
  // Only fetches if the tab has not been loaded yet for the current applicant.
  // Accepts an optional `app` override for use before selectedApp state settles.
  const handleLazyLoadTab = async (tab: string, app?: Application) => {
    const target = app || selectedApp;
    if (!target) return;
    if (loadedTabs.current.has(tab)) return; // already loaded
    loadedTabs.current.add(tab);

    if (tab === 'documents') {
      try {
        const appIdToFetch = (target as any).id || target.applicationId;
        const res = await safeFetch(`/api/admin/applications/${encodeURIComponent(appIdToFetch)}/documents`);
        const docs: DocumentMetadata[] = res?.documents || [];
        const resolved = await Promise.all(
          docs.map(async (d) => ({
            ...d,
            downloadUrl: await getCachedUrl(d.storagePath, d.downloadUrl),
          }))
        );
        setDocsList(resolved);
      } catch (err) {
        console.warn("Lazy-load documents failed:", err);
      }
    }

    if (tab === 'activity') {
      // Fetch comments and audit logs scoped to this applicant
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/admin/applications/${encodeURIComponent(target.applicationId)}/comments`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workspaceHeader() },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        setComments(data.comments || []);
      } catch (err) {
        console.warn("Lazy-load comments failed:", err);
      }
      await fetchAuditLogs(target.applicationId);
    }
  };


  const isSelectedAppActive = () => selectedApp ? (selectedApp.activeStatus ?? selectedApp.active ?? true) : true;
  const ensureSelectedAppMutable = () => {
    if (isSelectedAppActive()) return true;
    showToast('This application is inactive and cannot be modified until reactivated.', 'error');
    return false;
  };

  // Decode a JWT payload (idempotent, no deps) to inspect custom claims.
  const decodeJwtPayload = (token: string): any => {
    try {
      const part = token.split('.')[1];
      const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  };

  // Admin writes that stay on the client SDK (PII fallback + subcollections)
  // are gated by Firestore rules on `request.auth.token.admin == true`. A stale
  // or missing custom claim makes them fail with "Missing or insufficient
  // permissions". This helper DETERMINISTICALLY gets a claim-bearing token:
  //  1. If the cached token already carries admin:true → done (no network).
  //  2. Otherwise force-refresh and check the decoded claim.
  //  3. If still missing, ask the server to re-sync the claim from the user
  //     profile (requireAuth self-heals), refresh again, and wait for the
  //     Firestore SDK to observe the new token.
  const ensureAdminClaimReady = async (): Promise<void> => {
    try {
      let token = await auth.currentUser?.getIdToken();
      if (token && decodeJwtPayload(token)?.admin === true) return;

      token = await auth.currentUser?.getIdToken(true);
      if (token && decodeJwtPayload(token)?.admin === true) {
        await new Promise(resolve => setTimeout(resolve, 150));
        return;
      }

      if (token) {
        try {
          await fetch('/api/admin/verify-admin-claim', {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch { /* best-effort */ }
      }
      await auth.currentUser?.getIdToken(true);
      // Give the Firestore SDK a beat to observe the refreshed token.
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch { /* best-effort */ }
  };

  // Non-PII profile fields are written through the server Admin SDK so they
  // bypass Firestore security rules entirely — immune to stale/missing admin
  // claims on the client token.
  const adminProfileUpdate = async (applicationId: string, fields: Record<string, any>): Promise<void> => {
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error('Not authenticated');

    // Resolve target application workspaceId from local applications cache to attach workspace header
    const app = (applicationsRef.current || []).find((a: any) => a.applicationId === applicationId || a.id === applicationId);
    const appWorkspaceId = app?.workspaceId;

    const res = await fetch(`/api/admin/applications/${encodeURIComponent(applicationId)}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(appWorkspaceId ? { 'x-workspace-id': appWorkspaceId } : workspaceHeader()),
      },
      body: JSON.stringify({ fields }),
    });
    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json() : {};
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  };

  // P0-1a: PII fields are sent as PLAINTEXT over the authenticated
  // channel and encrypted BY THE SERVER before storage (the client
  // no longer holds the DEK). The Admin SDK write still bypasses
  // Firestore rules so a stale/missing `admin: true` claim can never
  // block an admin save.
  const piiProfileUpdate = async (applicationId: string, fields: Record<string, any>): Promise<void> => {
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error('Not authenticated');
    const res = await fetch(`/api/admin/applications/${encodeURIComponent(applicationId)}/pii`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fields }),
    });
    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json() : {};
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  };

  // Return the current user's Firebase ID token (used by child components for
  // authenticated API calls). Added to the context so ApplicantDetail and
  // other admin views can call appCtx.getToken().
  const getToken = async (): Promise<string | null> => {
    try {
      const user = auth.currentUser;
      if (!user) return null;
      return await user.getIdToken();
    } catch {
      return null;
    }
  };

  const syncSelectedApplication = (updated: Application) => {
    setSelectedApp(updated);
    const updatedList = applications.map(a => a.applicationId === updated.applicationId ? updated : a);
    setApplications(updatedList);
    calculateStats(updatedList);
    clearApplicationsCache();
  };

  const handleArchiveSelected = async () => {
    if (selectedArchivedIds.size === 0) return;
    setArchiving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/applications/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ applicationIds: Array.from(selectedArchivedIds) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Archive failed');
      // Refresh applications list and clear selection
      setSelectedArchivedIds(new Set());
      setShowArchiveModal(false);
      clearApplicationsCache();
      await fetchApplications();
    } catch (err: any) {
      console.error('Archive error:', err.message);
      setToast({ type: 'error', message: err.message || 'Failed to archive applications' });
    } finally {
      setArchiving(false);
    }
  };

  const handleToggleApplicationActive = async (targetAppId?: string) => {
    const targetApp = targetAppId ? applications.find(a => a.applicationId === targetAppId) : selectedApp;
    if (!targetApp) return;
    const nextActive = !isApplicationActive(targetApp);
    const now = new Date().toISOString();
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/applications/${targetApp.applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ activeStatus: nextActive, active: nextActive }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const updated = { ...targetApp, activeStatus: nextActive, active: nextActive, updatedAt: now };
      if (selectedApp && selectedApp.applicationId === targetApp.applicationId) {
        syncSelectedApplication(updated);
      } else {
        const updatedList = applications.map(a => a.applicationId === targetApp.applicationId ? updated : a);
        setApplications(updatedList);
        calculateStats(updatedList);
        clearApplicationsCache();
      }
      showToast(`Application marked ${nextActive ? 'active' : 'inactive'}.`);
      await createAuditLog('TOGGLE_APPLICATION_ACTIVE', targetApp.applicationId, `Application marked ${nextActive ? 'active' : 'inactive'}`);

      if (!nextActive) {
        const { emailApplicationDeactivated } = await import('../../../lib/emailTriggers');
        const { confirmNotification } = await import('../../../lib/notifications');
        if (await confirmNotification('Send a deactivation notice to the applicant?')) {
          await emailApplicationDeactivated(targetApp);
        }
      }
    } catch (err) {
      console.error('Failed to toggle application active state:', err);
      showToast('Failed to update application activity state', 'error');
    }
  };

  // 1. Update Application status
  const handleMasterStageChange = async (stage: number) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    setMasterStage(stage);
    try {
      const now = new Date().toISOString();
      // Use server API to bypass Firestore permission issues
      await safeFetch(`/api/admin/applications/${selectedApp.applicationId}/master-stage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterStage: stage }),
      });
      syncSelectedApplication({ ...selectedApp, masterStage: stage, updatedAt: now });
      showToast(`Pipeline stage updated to: ${MASTER_STAGES[stage].label}`);
    } catch (err: any) {
      console.error('Failed to update master stage:', err);
      showToast(err.message || 'Failed to update pipeline stage', 'error');
    }
  };

  const handleReturnToApplicant = async () => {
    if (!selectedApp || !selectedApp.applicationId || !ensureSelectedAppMutable()) return;
    if (!(await confirmDialog('Return this application to the applicant? They will be able to continue editing and resubmit.'))) return;
    try {
      // Use the server API (Admin SDK) to bypass Firestore security rules
      const token = await auth.currentUser?.getIdToken();
      const note = 'Please review and resubmit.';
      const res = await fetch(`/api/admin/applications/${selectedApp.applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          status: ApplicationStatus.RETURNED,
          submissionStatus: ApplicationStatus.RETURNED,
          returnNote: note,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      setSelectedApp({ ...selectedApp, submissionStatus: ApplicationStatus.RETURNED, status: ApplicationStatus.RETURNED, updatedAt: new Date().toISOString() });
      setApplications(applications.map(a => a.applicationId === selectedApp.applicationId ? { ...a, submissionStatus: ApplicationStatus.RETURNED, status: ApplicationStatus.RETURNED } : a));
      showToast('Application returned to applicant for editing.');
      await createAuditLog('RETURN_TO_APPLICANT', selectedApp.applicationId, `Application returned to applicant for revisions. Note: ${note}`);

      // A-2: Auto-notify applicant when application is returned
      const { emailApplicationReturned } = await import('../../../lib/emailTriggers');
      await emailApplicationReturned(selectedApp);
    } catch (err) {
      console.error('Failed to return application:', err);
      showToast('Failed to return application', 'error');
    }
  };

  // Returns true only when the status change actually committed.
  // Callers that gate UI on the outcome (e.g. the ApplicantDetail
  // status-change confirmation modal) MUST check the result: failures
  // are surfaced here (toast/alert) but historically were swallowed,
  // which let the modal report success for a change that never happened.
  const handleUpdateStatus = async (appId: string, newStatus: ApplicationStatus): Promise<boolean> => {
    if (selectedApp?.applicationId === appId && !ensureSelectedAppMutable()) return false;
    try {
      // Use the server API (Admin SDK) to bypass Firestore security rules
      const token = await auth.currentUser?.getIdToken();
      const body: Record<string, any> = {
        status: newStatus,
        submissionStatus: newStatus,
      };
      if (newStatus === ApplicationStatus.SUBMITTED) {
        body.masterStage = 1;
      }
      const res = await fetch(`/api/admin/applications/${appId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      
      // Update local state
      const updatedList = applications.map(a => {
        if (a.applicationId === appId) {
          const u = { ...a, submissionStatus: newStatus, status: newStatus, masterStage: newStatus === ApplicationStatus.SUBMITTED ? 1 : a.masterStage, updatedAt: new Date().toISOString() };
          if (selectedApp?.applicationId === appId) setSelectedApp(u);
          return u;
        }
        return a;
      });
      setApplications(updatedList as Application[]);
      calculateStats(updatedList as Application[]);

      // Logger
      await createAuditLog('UPDATE_STATUS', appId, `Changed status to ${newStatus}`);

      // Email triggers — the status change has already committed at
      // this point, so a failed notification must be surfaced WITHOUT
      // flipping the result to failure or corrupting the change.
      const app = updatedList.find(a => a.applicationId === appId);
      try {
      if (app) {
        if (newStatus === ApplicationStatus.SUBMITTED) {
          // A-3: Auto-notify applicant when application is approved/submitted
          const { emailApplicationApproved } = await import('../../../lib/emailTriggers');
          await emailApplicationApproved(app);
        } else if (newStatus === ApplicationStatus.RETURNED) {
          // A-2: Auto-notify applicant when application is returned
          const { emailApplicationReturned } = await import('../../../lib/emailTriggers');
          await emailApplicationReturned(app);
        } else if (newStatus === ApplicationStatus.CANCELLED) {
          // A-4: Auto-notify on cancellation
          const { emailApplicationCancelled } = await import('../../../lib/emailTriggers');
          await emailApplicationCancelled(app);
        } else if (newStatus === ApplicationStatus.ARCHIVED) {
          // A-6: Auto-notify on archive
          const { emailApplicationArchived } = await import('../../../lib/emailTriggers');
          await emailApplicationArchived(app);
        }
      }
      } catch (emailErr) {
        console.error('Status updated but the notification email failed:', emailErr);
        showToast('Status updated, but the notification email failed to send.', 'error');
      }
      return true;
    } catch (err) {
      console.error("Status update error:", err);
      notifyToast('error', "Failed updating status. Checking role permissions.");
      return false;
    }
  };

  // 2. Assign Service Provider
  const handleAssignProvider = async (providerId: string) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;

    // Allow clearing the assignment
    const providerObj = providerId ? providers.find(p => p.providerId === providerId) : null;

    try {
      const updateData: any = {};
      if (providerObj) {
        // Store partner ID as source of truth — display code resolves name dynamically
        updateData.assignedPartner = providerId;
        updateData.serviceProvider = providerObj;
      } else {
        updateData.serviceProvider = null;
        updateData.assignedPartner = null;
      }
      // Routed through the server Admin SDK — bypasses Firestore rules so a
      // stale/missing admin claim can never block the write.
      await adminProfileUpdate(selectedApp.applicationId, updateData);

      // Update local state
      const updated = { ...selectedApp, ...updateData, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('ASSIGN_PROVIDER', selectedApp.applicationId, providerObj
        ? `Assigned Sponsor: ${providerObj.name}`
        : `Removed sponsor assignment`);

      // SP-1: Notify applicant when sponsor assigned
      if (providerObj) {
        const { emailSponsorAssigned } = await import('../../../lib/emailTriggers');
        await emailSponsorAssigned(selectedApp, providerObj.name);
      }

      notifyToast('success', providerObj
        ? `Successfully assigned ${providerObj.name} to this application.`
        : `Sponsor assignment removed.`);
    } catch (err) {
      console.error("Assignment failed:", err);
    }
  };

  // --- Administrative Section Modification Handlers ---

  const handleUpdateAdminNotes = async (notes: string) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      const adminName = auth.currentUser?.displayName || auth.currentUser?.email || 'Administrator';
      const timestamp = new Date().toISOString();
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, {
        adminNotes: notes,
        adminNotesModifiedBy: adminName,
        adminNotesModifiedAt: timestamp,
      });

      const updated = {
        ...selectedApp,
        adminNotes: notes,
        adminNotesModifiedBy: adminName,
        adminNotesModifiedAt: timestamp,
        updatedAt: timestamp
      };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_ADMIN_NOTES', selectedApp.applicationId, `Updated administrative notes`);
    } catch (err) {
      console.error("Notes update failed:", err);
      notifyToast('error', "Failed to save notes. Please try again.");
    }
  };

  const handleUpdateApplicantTags = async (applicationId: string, tags: string[]) => {
    try {
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(applicationId, { tags });
      setApplications(prev => prev.map(a => a.applicationId === applicationId ? { ...a, tags } : a));
      if (selectedApp?.applicationId === applicationId) {
        setSelectedApp({ ...selectedApp, tags } as any);
      }
      clearApplicationsCache();
      const tagStr = tags.length > 0 ? tags.join(', ') : '(none)';
      createAuditLog('UPDATE_TAGS', applicationId, `Tags updated: ${tagStr}`).catch(() => {});
    } catch (err) {
      console.error("Failed to update applicant tags:", err);
      notifyToast('error', "Failed to update tags. Please try again.");
    }
  };

  const handleSavePersonal = async (personalData: any) => {
    if (!selectedApp || !personalData || !ensureSelectedAppMutable()) return;
    try {
      // Merge with existing personalDetails to preserve non-edited fields
      const merged = { ...(selectedApp.personalDetails || {}), ...personalData };
      // Strip undefined values — Firestore rejects them
      Object.keys(merged).forEach(key => {
        if (merged[key] === undefined) delete merged[key];
      });
      // PII is encrypted client-side then stored via the server Admin SDK
      // (bypasses Firestore rules). Falls back to the client SDK write if the
      // server endpoint is not deployed yet.
      try {
        await piiProfileUpdate(selectedApp.applicationId, { personalDetails: merged });
      } catch (serverErr) {
        console.warn('Server-side PII save failed, falling back to client SDK:', serverErr);
        await ensureAdminClaimReady();
        await updateDoc(doc(db, 'applications', selectedApp.applicationId), { personalDetails: merged, updatedAt: new Date().toISOString() });
      }

      const updated = { ...selectedApp, personalDetails: merged, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_PERSONAL_DETAILS', selectedApp.applicationId, `Admin edited candidate demographics`);
      setIsEditingPersonal(false);
      notifyToast('success', "Demographics saved successfully.");
    } catch (err) {
      console.error("Saving personal failed:", err);
      notifyToast('error', "Failed to save demographics changes.");
    }
  };

  const handleSavePassport = async (_passportData: any) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      const passportData = {
        passportNumber: editPassportNo,
        passportCountry: editPassportCountry,
        issueDate: editPassportIssue,
        expirationDate: editPassportExpiry,
        visaStatus: editPassVisaStatus,
        ds2019Number: editDs2019,
        sevisId: editSevisId,
        intendedArrival: editIntendedArrival,
        usDestination: editUsDestination,
      };
      // PII is encrypted client-side then stored via the server Admin SDK
      // (bypasses Firestore rules). Falls back to the client SDK write if the
      // server endpoint is not deployed yet.
      try {
        await piiProfileUpdate(selectedApp.applicationId, { passportImmigration: passportData });
      } catch (serverErr) {
        console.warn('Server-side PII save failed, falling back to client SDK:', serverErr);
        await ensureAdminClaimReady();
        await updateDoc(doc(db, 'applications', selectedApp.applicationId), { passportImmigration: passportData, updatedAt: new Date().toISOString() });
      }

      const updated = { ...selectedApp, passportImmigration: passportData, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_PASSPORT_IMMIGRATION', selectedApp.applicationId, `Admin edited passport & visa fields`);
      setIsEditingPassport(false);

      // V-1: DS-2019 number entered
      const prevDs2019 = selectedApp.passportImmigration?.ds2019Number;
      if (editDs2019 && editDs2019 !== prevDs2019) {
        const { emailDs2019Ready } = await import('../../../lib/emailTriggers');
        await emailDs2019Ready(selectedApp);
      }

      // V-2: SEVIS ID assigned
      const prevSevis = selectedApp.passportImmigration?.sevisId;
      if (editSevisId && editSevisId !== prevSevis) {
        const { emailSevisIdAssigned } = await import('../../../lib/emailTriggers');
        await emailSevisIdAssigned(selectedApp, editSevisId);
      }

      // V-3 / V-4: Visa status changed
      const prevVisaStatus = selectedApp.passportImmigration?.visaStatus;
      if (editPassVisaStatus && editPassVisaStatus !== prevVisaStatus) {
        if (editPassVisaStatus === 'Approved') {
          const { emailVisaApproved } = await import('../../../lib/emailTriggers');
          await emailVisaApproved(selectedApp);
        } else if (editPassVisaStatus === 'Rejected' || editPassVisaStatus === 'Denied') {
          const { emailVisaDenied } = await import('../../../lib/emailTriggers');
          await emailVisaDenied(selectedApp);
        }
      }

      // SP-3: Intended arrival date set
      const prevArrival = selectedApp.passportImmigration?.intendedArrival;
      if (editIntendedArrival && editIntendedArrival !== prevArrival) {
        const { emailArrivalDateSet } = await import('../../../lib/emailTriggers');
        await emailArrivalDateSet(selectedApp, editIntendedArrival);
      }

      notifyToast('success', "Passport details saved successfully.");
    } catch (err) {
      console.error("Saving passport failed:", err);
      notifyToast('error', "Failed to save passport fields.");
    }
  };

  const handleSaveEducation = async (edList: any[]) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      // Routed through the server Admin SDK — bypasses Firestore rules so the
      // save works even when the client token is stale/missing the admin claim.
      await adminProfileUpdate(selectedApp.applicationId, { educationalBackground: edList });

      const updated = { ...selectedApp, educationalBackground: edList, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_EDUCATION_RECORD', selectedApp.applicationId, `Admin restructured academic records`);
      setIsEditingEducation(false);
      notifyToast('success', "Academic history saved successfully.");
    } catch (err) {
      console.error("Saving education list failed:", err);
      notifyToast('error', "Failed to save academic history. Please try again.");
    }
  };

  const handleSaveWork = async (wList: any[]) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      await adminProfileUpdate(selectedApp.applicationId, { workExperience: wList });

      const updated = { ...selectedApp, workExperience: wList, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_WORK_RECORD', selectedApp.applicationId, `Admin modified vocational listings`);
      setIsEditingWork(false);
      notifyToast('success', "Employment history saved successfully.");
    } catch (err) {
      console.error("Saving work failed:", err);
      notifyToast('error', "Failed to save employment history. Please try again.");
    }
  };

  const handleSaveEmergency = async (emList: any[]) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      // Emergency contacts are PII-encrypted client-side, then stored via the
      // server Admin SDK (bypasses Firestore rules). Falls back to the client
      // SDK write if the server endpoint is not deployed yet.
      try {
        await piiProfileUpdate(selectedApp.applicationId, { emergencyContacts: emList });
      } catch (serverErr) {
        console.warn('Server-side PII save failed, falling back to client SDK:', serverErr);
        await ensureAdminClaimReady();
        await updateDoc(doc(db, 'applications', selectedApp.applicationId), { emergencyContacts: emList, updatedAt: new Date().toISOString() });
      }

      const updated = { ...selectedApp, emergencyContacts: emList, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('EDIT_EMERGENCY_CONTACTS', selectedApp.applicationId, `Admin updated emergency and sponsor contact records`);
      setIsEditingEmergency(false);
      notifyToast('success', "Emergency contacts saved successfully.");
    } catch (err) {
      console.error("Saving emergency failed:", err);
      notifyToast('error', "Failed to save emergency contacts. Please try again.");
    }
  };

  const handleSaveProgram = async (progData: any) => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      const updateFields: Record<string, any> = {};

      // The Placement tab's "Program Assignment" form passes { programType, programTerm };
      // the Profile tab's "Program Preferences" form calls handleSaveProgram({}) and only
      // edits preferences — it must not touch the assigned program/term.
      const isAssignmentSave = progData && (progData.programType !== undefined || progData.programTerm !== undefined);

      let selected: any = null;
      // If progData contains programType, resolve the program ID and name
      if (progData.programType) {
        const progList = (typeof programsList !== 'undefined' ? programsList : []) as any[];
        selected = progList.find((p: any) => p.id === progData.programType || p.programId === progData.programType);
        updateFields.programId = selected ? (selected.id || selected.programId) : (selectedApp.programId || progData.programType);
        updateFields.programType = selected ? selected.name : (selectedApp.programType || progData.programType);

        // Process Pricing Variable if provided
        const targetVarId = progData.pricingVariableId;
        if (targetVarId) {
          const pVars = Array.isArray(selected?.pricingVariables) ? selected.pricingVariables : [];
          const matchedVar = pVars.find((v: any) => v.id === targetVarId);
          if (matchedVar) {
            updateFields.pricingVariableId = matchedVar.id;
            updateFields.pricingVariableName = matchedVar.name;
            updateFields.pricingVariableCode = matchedVar.code || '';
            updateFields.pricingVariableSellingPrice = Number(matchedVar.sellingPrice || 0);
            updateFields.pricingVariableCostPrice = Number(matchedVar.costPrice || 0);
            updateFields.pricingVariableCurrency = matchedVar.currency || 'USD';
            updateFields.pricingVariableSnapshot = {
              pricingVariableId: matchedVar.id,
              pricingVariableName: matchedVar.name,
              pricingVariableCode: matchedVar.code || '',
              pricingVariableSellingPrice: Number(matchedVar.sellingPrice || 0),
              pricingVariableCostPrice: Number(matchedVar.costPrice || 0),
              pricingVariableCurrency: matchedVar.currency || 'USD',
              snapshottedAt: new Date().toISOString(),
            };
          }
        }

        // Revalidate existing Sponsor assignment against ACD configuration
        const acds = Array.isArray(selected?.acdRecords) ? selected.acdRecords : [];
        const currentSponsorId = progData.sponsorId || selectedApp.sponsorId || (selectedApp as any).assignedSponsor;
        if (currentSponsorId && targetVarId && acds.length > 0) {
          const validAcd = acds.find((a: any) => a.pricingVariableId === targetVarId && a.sponsorId === currentSponsorId && a.active !== false);
          if (!validAcd) {
            updateFields.sponsorIneligible = true;
            updateFields.sponsorIneligibleReason = `Current sponsor is not linked via an active Actual Cost Determination (ACD) to the selected Pricing Variable.`;
          } else {
            updateFields.sponsorIneligible = false;
            updateFields.sponsorIneligibleReason = null;
          }
        }
      }

      // Resolve the selected term to its display label — terms may be plain strings
      // ("Fall 2027") or structured objects ({ termId, label, startDate, ... }).
      // Only assignment saves touch programTerm; preference-only saves preserve it.
      const rawTerm = isAssignmentSave ? (progData.programTerm || editProgramTerm || '') : '';
      let term = typeof rawTerm === 'string' ? rawTerm.trim() : '';
      if (term && selected?.terms?.length) {
        const match = selected.terms.find((t: any) => {
          if (typeof t === 'string') return t === term;
          return t?.termId === term || t?.label === term || t?.name === term;
        });
        if (match && typeof match === 'object' && (match.label || match.name)) {
          term = match.label || match.name || term;
        }
      }

      // Merge with existing programPreferences to avoid losing fields
      // Collect preferences form fields from state variables
      updateFields.programPreferences = {
        ...(selectedApp.programPreferences || {}),
        preferredIndustry: editProgIndustry,
        preferredState: editProgState,
        startDateAvailability: editProgStart,
        programDuration: editProgDuration,
        programTerm: term || selectedApp.programPreferences?.programTerm || '',
        englishProficiency: editProgEnglish,
        specialSkills: editProgSkills,
        additionalNotes: editProgNotes,
        willingnessToRelocate: editProgRelocate,
        programType: updateFields.programType || progData.programType || selectedApp.programPreferences?.programType || '',
      };

      // Save programTerm at root level (primary field), assignment (legacy
      // access), and programPreferences (form field) for consistent reads.
      if (isAssignmentSave) {
        updateFields.programTerm = term;
        if (term || selectedApp.assignment) {
          updateFields.assignment = {
            ...(selectedApp.assignment || {}),
            programTerm: term,
          };
        }
      }

      // Track old program for SP-2 trigger
      const oldProgram = selectedApp.programType;

      // Routed through the server Admin SDK — bypasses Firestore rules so the
      // save works even when the client token is stale/missing the admin claim.
      await adminProfileUpdate(selectedApp.applicationId, updateFields);

      const updated = { ...selectedApp, ...updateFields, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);

      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();

      await createAuditLog('EDIT_PROGRAM', selectedApp.applicationId, `Admin updated program assignment`);
      setIsEditingProgram(false);

      // SP-2: Program type changed
      if (updateFields.programType && updateFields.programType !== oldProgram) {
        const { emailProgramChanged } = await import('../../../lib/emailTriggers');
        await emailProgramChanged(selectedApp, oldProgram, updateFields.programType);
      }

      // P-2: Start date confirmed
      const oldStart = selectedApp.programPreferences?.startDateAvailability || '';
      if (editProgStart && editProgStart !== oldStart) {
        const { emailPlacementStartDateConfirmed } = await import('../../../lib/emailTriggers');
        await emailPlacementStartDateConfirmed(selectedApp, editProgStart);
      }

      notifyToast('success', "Program saved successfully.");
    } catch (err) {
      console.error("Saving program failed:", err);
      notifyToast('error', "Failed to save program.");
    }
  };

  // Upload document on behalf of the applicant
  const runDocValidation = async (docMeta: DocumentMetadata, file: File): Promise<DocumentMetadata> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/documents/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          docId: docMeta.docId,
          applicationId: docMeta.applicationId,
          storagePath: docMeta.storagePath,
          fileName: docMeta.name,
          fileType: docMeta.type,
          mimeType: file.type,
          contentLength: file.size,
        }),
      });
      if (!res.ok) return { ...docMeta, status: 'PendingAdminReview', validationComplete: true };
      const data = await res.json();
      if (!data.success) return { ...docMeta, status: 'PendingAdminReview', validationComplete: true };

      let newStatus: DocumentMetadata['status'];
      const failedCheckNotes: string[] = (data.validation?.checks || [])
        .filter((c: any) => !c.passed)
        .map((c: any) => c.details || c.name);
      // Rejection reasons belong to an actual Rejected verdict only — a
      // PendingAdminReview routing is not a rejection and is no longer
      // logged as one.
      const rejectionReasons: string[] = [];
      if (data.suggestedStatus === 'SystemPreApproved') {
        newStatus = 'SystemPreApproved';
      } else if (data.suggestedStatus === 'PendingAdminReview') {
        newStatus = 'PendingAdminReview';
      } else {
        newStatus = 'Rejected';
        rejectionReasons.push(...failedCheckNotes);
      }

      const logAction = newStatus as 'SystemPreApproved' | 'PendingAdminReview' | 'Rejected';
      const logNote = newStatus === 'Rejected'
        ? (rejectionReasons.join('; ') || 'System validation failed')
        : newStatus === 'PendingAdminReview'
          ? (failedCheckNotes.join('; ') || 'Automated checks did not fully pass — routed to admin review')
          : 'System validation completed';

      return {
        ...docMeta,
        status: newStatus,
        rejectionNote: rejectionReasons.length > 0 ? rejectionReasons.join('; ') : null,
        rejectionReasons: rejectionReasons.length > 0 ? rejectionReasons : null,
        aiValidation: { passed: data.validation.passed, checkedAt: data.validation.checkedAt, checks: data.validation.checks },
        aiReview: data.aiReview || undefined,
        validationComplete: true,
        reviewLogs: [{
          id: 'sys_' + Date.now(),
          action: logAction,
          note: logNote,
          rejectionReasons: rejectionReasons.length > 0 ? rejectionReasons : null,
          by: 'system', byName: 'Automated System', at: new Date().toISOString(),
          aiAssisted: data.aiRan === true,
          aiModel: data.aiReview?.model || undefined,
        }],
      };
    } catch { return { ...docMeta, status: 'PendingAdminReview', validationComplete: true }; }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] || result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAdminUploadDocument = async (fileOverride?: File, onValidationUpdate?: (status: 'waiting' | 'checking' | 'complete') => void, skipValidation?: boolean) => {
    const uploadFile = fileOverride || adminUploadedFile;
    if (!selectedApp || !uploadFile) {
      showToast("Please choose a file to upload first", 'error');
      return;
    }
    if (!isAcceptableDocumentFileName(uploadFile.name)) {
      showToast(unsupportedTypeMessage(uploadFile.name), 'error');
      return;
    }
    if (selectedApp.submissionStatus === 'Closed' || selectedApp.status === 'Closed') {
      showToast('Cannot upload documents — application is closed.', 'error');
      return;
    }
    const isImg = uploadFile.type.startsWith('image/');
    const isPdf = uploadFile.type === 'application/pdf';
    if (!isImg && uploadFile.size > 10 * 1024 * 1024) {
      showToast('Documents must be under 10MB', 'error'); return;
    }
    if (isPdf && uploadFile.size > 10 * 1024 * 1024) {
      showToast('PDF files must be under 10MB', 'error'); return;
    }
    setAdminUploading(true);
    setAdminUploadProgress(0);
    const fileName = uploadFile.name;

    try {
      let uploadPayload = uploadFile;
      if (isImg) uploadPayload = await compressImage(uploadPayload);

      // Server-side upload (Admin SDK storage + Firestore subcollection) — works
      // even when the admin token lacks the claim and when the application has
      // no userId yet. Falls back to the client SDK path for older deployments.
      let docMeta: DocumentMetadata | null = null;
      try {
        const token = await auth.currentUser?.getIdToken(true);
        if (!token) throw new Error('Not authenticated');
        const fileBase64 = await fileToBase64(uploadPayload);
        const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/documents/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
          body: JSON.stringify({ fileName, docType: adminDocType, fileBase64, meta: {} }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        docMeta = data.docMeta as DocumentMetadata;
        triggerDocumentExtraction(docMeta.docId, selectedApp.applicationId); // background extraction
      } catch (serverErr) {
        console.warn('Server-side document upload failed, falling back to client SDK:', serverErr);
        await ensureAdminClaimReady();
        const docId = 'doc_' + crypto.randomUUID();
        const storageRef = ref(storage, `applications/${selectedApp.userId || 'unknown'}/${selectedApp.applicationId}/${adminDocType}/${docId}_${fileName}`);
        const snapshot = await uploadBytesWithTimeout(storageRef, uploadPayload, 60000, 3, (p) => setAdminUploadProgress(p));
        docMeta = {
          docId, applicationId: selectedApp.applicationId, userId: selectedApp.userId,
          name: fileName, type: adminDocType, uploadDate: new Date().toISOString(),
          status: 'Pending', storagePath: getUploadedStoragePath(snapshot), downloadUrl: snapshot.downloadUrl
        };
        const cleanMeta = JSON.parse(JSON.stringify(docMeta, (k, v) => v === undefined ? null : v));
        await setDoc(doc(db, 'applications', selectedApp.applicationId, 'documents', docId), cleanMeta);
        triggerDocumentExtraction(docId, selectedApp.applicationId);
      }

      // Run AI validation (needs the storage object, so it runs after upload)
      if (!skipValidation && docMeta) {
        onValidationUpdate?.('checking');
        docMeta = await runDocValidation(docMeta, uploadPayload);
        onValidationUpdate?.('complete');
        // Apply the validation result through the server (Admin SDK) so it works
        // regardless of the token claim.
        try {
          await safeFetch('/api/admin/documents/update-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              applicationId: selectedApp.applicationId,
              docId: docMeta.docId,
              newStatus: docMeta.status,
              aiValidation: docMeta.aiValidation,
              reviewLogs: docMeta.reviewLogs,
              rejectionNote: docMeta.rejectionNote,
              rejectionReasons: docMeta.rejectionReasons,
              validationComplete: docMeta.validationComplete,
              aiReview: docMeta.aiReview,
            }),
          });
        } catch (applyErr) {
          console.warn('Failed to apply validation status:', applyErr);
        }
      }

      if (adminDocType.toLowerCase() === 'photo' && docMeta) {
        await safeFetch(`/api/admin/applications/${selectedApp.applicationId}/update-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profilePhotoUrl: docMeta.downloadUrl }),
        });
        const updatedApp = { ...selectedApp, profilePhotoUrl: docMeta.downloadUrl, updatedAt: new Date().toISOString() };
        setSelectedApp(updatedApp); setProfilePhotoUrl(docMeta.downloadUrl);
        setApplications(applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a));
      }

      if (docMeta) {
        const updatedDocs = [...docsList, docMeta];
        setDocsList(updatedDocs);
        try { await createAuditLog('UPLOAD_DOCUMENT_ON_BEHALF', selectedApp.applicationId, `Admin uploaded ${adminDocType} file: ${fileName}`); } catch (e) { console.warn(e); }
      }

      setAdminUploadedFile(null);
      const fi = document.getElementById('admin-file-upload-input') as HTMLInputElement; if (fi) fi.value = '';
      const fi2 = document.getElementById('admin-file-upload-input-bento') as HTMLInputElement; if (fi2) fi2.value = '';
      showToast(`Successfully uploaded ${fileName}!`);
    } catch (err) {
      console.error("Upload failed:", err);
      showToast(getStorageErrorMessage(err), 'error');
    } finally {
      setAdminUploading(false);
      setAdminUploadProgress(null);
    }
  };

  // Replace document on behalf of the applicant
  const handleAdminReplaceDocument = async (docId: string, docType: string, file: File) => {
    if (!selectedApp || !file || !ensureSelectedAppMutable()) return;
    setAdminUploading(true);
    const fileName = file.name;
    setAdminUploadProgress(0);

    try {
      let uploadFile = file;
      if (uploadFile.type.startsWith('image/')) uploadFile = await compressImage(uploadFile);

      let finalUrl = '';
      let storagePath = '';

      try {
        const token = await auth.currentUser?.getIdToken(true);
        if (!token) throw new Error('Not authenticated');
        const fileBase64 = await fileToBase64(uploadFile);
        const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/documents/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
          body: JSON.stringify({ fileName, docType, fileBase64, meta: { status: 'Approved' } }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        if (data.docMeta) {
          finalUrl = data.docMeta.downloadUrl || data.downloadUrl;
          storagePath = data.docMeta.storagePath || data.storagePath;
          triggerDocumentExtraction(data.docMeta.docId, selectedApp.applicationId);
        }
      } catch (serverErr) {
        console.warn('Server-side document replace failed, falling back to client SDK:', serverErr);
        await ensureAdminClaimReady();
        const storageRef = ref(storage, `applications/${selectedApp.userId || 'unknown'}/${selectedApp.applicationId}/${docType}/${docId}_${fileName}`);
        const snapshot = await uploadBytesWithTimeout(storageRef, uploadFile, 60000, 3, (p) => setAdminUploadProgress(p));
        finalUrl = snapshot.downloadUrl;
        storagePath = getUploadedStoragePath(snapshot);

        const updatedFields = { name: fileName, uploadDate: new Date().toISOString(), storagePath, downloadUrl: finalUrl, status: 'Approved' };
        await updateDoc(doc(db, 'applications', selectedApp.applicationId, 'documents', docId), updatedFields);
        triggerDocumentExtraction(docId, selectedApp.applicationId);
      }

      if (docType.toLowerCase() === 'photo' && finalUrl) {
        await safeFetch(`/api/admin/applications/${selectedApp.applicationId}/update-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profilePhotoUrl: finalUrl }),
        });
        const updatedApp = { ...selectedApp, profilePhotoUrl: finalUrl, updatedAt: new Date().toISOString() };
        setSelectedApp(updatedApp); setProfilePhotoUrl(finalUrl);
        setApplications(applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a));
      }

      const updatedFields = { name: fileName, uploadDate: new Date().toISOString(), storagePath, downloadUrl: finalUrl, status: 'Approved' };
      const updatedDocs = docsList.map(d => d.docId === docId ? { ...d, ...updatedFields } : d);
      setDocsList(updatedDocs as DocumentMetadata[]);
      try { await createAuditLog('REPLACE_DOCUMENT', selectedApp.applicationId, `Admin replaced ${docType} file with: ${fileName}`); } catch (e) { console.warn(e); }

      showToast(`Successfully replaced with new revision: ${fileName}`);
    } catch (err) {
      console.error("Replacement failed:", err);
      showToast(getStorageErrorMessage(err), 'error');
    } finally {
      setAdminUploading(false);
      setAdminUploadProgress(null);
    }
  };

  // Delete document
  const handleAdminDeleteDocument = async (docId: string, docName: string) => {
    if (!selectedApp) return;
    if (!(await confirmDialog(`Are you sure you want to permanently delete document "${docName}"? This cannot be undone.`))) return;

    try {
      const targetDoc = docsList.find(d => d.docId === docId);

      // Server-side delete endpoint (Admin SDK — bypasses client Firestore rules)
      let serverDeleteSuccess = false;
      try {
        await safeFetch(`/api/applications/${encodeURIComponent(selectedApp.applicationId)}/documents/${encodeURIComponent(docId)}`, {
          method: 'DELETE',
        });
        serverDeleteSuccess = true;
      } catch (serverErr) {
        console.warn("Server-side delete document endpoint failed, falling back to client SDK:", serverErr);
      }

      if (!serverDeleteSuccess) {
        await ensureAdminClaimReady();
        try {
          const docRef = doc(db, 'applications', selectedApp.applicationId, 'documents', docId);
          await deleteDoc(docRef);
        } catch (dbErr) {
          console.warn("Firestore deleteDoc blocked:", dbErr);
        }

        if (targetDoc?.storagePath) {
          try {
            const storageRef = ref(storage, targetDoc.storagePath);
            await deleteObject(storageRef);
          } catch (stErr) {
            try {
              const rawPath = targetDoc.storagePath;
              const pathMatch = rawPath.match(/\/o\/([^?]+)/);
              if (pathMatch) {
                const decodedPath = decodeURIComponent(pathMatch[1]);
                const storageRef = ref(storage, decodedPath);
                await deleteObject(storageRef);
              }
            } catch (stErr2) {
              console.warn("Storage deleteObject failed (path may be URL-only):", stErr2);
            }
          }
        }
      }

      const updatedDocs = docsList.filter(d => d.docId !== docId);
      setDocsList(updatedDocs);

      try {
        await createAuditLog('DELETE_DOCUMENT', selectedApp.applicationId, `Admin deleted document: ${docName}`);
      } catch (dbErr) {
        console.warn("Audit log blocked:", dbErr);
      }
      showToast("Document deleted successfully.");
    } catch (err) {
      console.error("Error deleting doc:", err);
      showToast("Failed to delete document.", 'error');
    }
  };

  // 3. Document Approval/Rejection review — double-fire guarded.
  // The decision runs through applyDocumentDecision
  // (src/lib/documentReviewGuard.ts): a synchronous in-flight guard makes
  // a second rapid invocation return 'in-flight' without starting, and a
  // fresh-state check makes a replayed submission a 'deduplicated' no-op
  // success — one write, one reviewLog entry, one notification per
  // decision, while genuine re-decisions still write.
  const reviewDecisionInFlightRef = useRef<Set<string>>(new Set());
  const [reviewInFlightDocIds, setReviewInFlightDocIds] = useState<string[]>([]);

  const handleReviewDocument = async (
    docId: string,
    action: 'Approved' | 'Rejected',
    note = '',
    aiProvenance?: import('../../../types').AiReviewProvenance | null
  ): Promise<import('../../../lib/documentReviewGuard').DocumentDecisionOutcome> => {
    if (!selectedApp || !ensureSelectedAppMutable()) return 'failed';

    const adminName = auth.currentUser?.displayName || adminEmail.split('@')[0];
    const adminUid = auth.currentUser?.uid || 'unauthenticated';
    const now = new Date().toISOString();
    const applicationId = selectedApp.applicationId;
    const appForNotify = selectedApp;

    try {
      const result = await applyDocumentDecision(
        {
          inFlight: reviewDecisionInFlightRef.current,
          onInFlightChange: (changedDocId, inFlight) => {
            setReviewInFlightDocIds(prev =>
              inFlight
                ? (prev.includes(changedDocId) ? prev : [...prev, changedDocId])
                : prev.filter(d => d !== changedDocId)
            );
          },
          getDocument: async (appId, targetDocId) => {
            await ensureAdminClaimReady();
            const snap = await plainGetDoc(doc(db, 'applications', appId, 'documents', targetDocId));
            return snap.exists()
              ? ({ ...(snap.data() as Record<string, unknown>), docId: targetDocId } as Record<string, any>)
              : null;
          },
          updateDocument: async (appId, targetDocId, data) => {
            await updateDoc(doc(db, 'applications', appId, 'documents', targetDocId), data);
          },
          writeAuditLog: async (auditAction, appId, message) => {
            await createAuditLog(auditAction, appId, message);
          },
          notifyDecision: async ({ action: decidedAction, docName, note: decisionNote }) => {
            // D-2 / D-3: Auto-notify applicant of document decision (no prompt)
            if (decidedAction === 'Approved') {
              const { emailDocumentApproved } = await import('../../../lib/emailTriggers');
              await emailDocumentApproved(appForNotify, docName);
            } else {
              const { emailDocumentRejected } = await import('../../../lib/emailTriggers');
              await emailDocumentRejected(appForNotify, docName, decisionNote || undefined);
            }
          },
        },
        {
          applicationId,
          docId,
          action,
          note,
          aiProvenance: (aiProvenance as unknown as Record<string, any>) || null,
          adminUid,
          adminName,
          now,
        }
      );

      if (result.outcome === 'applied' && result.updateData) {
        // Update subcollection local state with the recorded write.
        const updateData = result.updateData;
        setDocsList(docsList.map(d => d.docId === docId ? { ...d, ...updateData } : d));
        // Close rejection modal after action completes
        setRejectionModalDocId(null);
      } else if (result.outcome === 'deduplicated' && result.doc) {
        // Already recorded (a repeat of the same decision): show the
        // recorded decision immediately; nothing is written again.
        const recorded = result.doc as unknown as DocumentMetadata;
        setDocsList(docsList.map(d => d.docId === docId ? { ...d, ...recorded } : d));
        setRejectionModalDocId(null);
      }
      return result.outcome;
    } catch (err) {
      console.error("Doc review update error:", err);
      return 'failed';
    }
  };

  // 4. Rename uploaded document
  const handleRenameDocument = async (docId: string) => {
    if (!selectedApp || !editingDocName.trim() || !ensureSelectedAppMutable()) return;

    // Find the original document to get its extension
    const originalDoc = docsList.find(d => d.docId === docId);
    if (!originalDoc) return;

    // Extract the extension from the original file name, e.g., ".pdf" or ".jpg"
    const lastDotIdx = originalDoc.name.lastIndexOf('.');
    const ext = lastDotIdx !== -1 ? originalDoc.name.substring(lastDotIdx) : '';

    // Build the final target name that secures the original extension
    let targetName = editingDocName.trim();
    // Strip user typed suffix or extension and enforce original
    const userDotIdx = targetName.lastIndexOf('.');
    if (userDotIdx !== -1) {
      targetName = targetName.substring(0, userDotIdx);
    }
    if (ext) {
      targetName = targetName + ext;
    }

    try {
      await ensureAdminClaimReady();
      try {
        const docRef = doc(db, 'applications', selectedApp.applicationId, 'documents', docId);
        await updateDoc(docRef, { name: targetName });
      } catch (dbErr) {
        console.warn("Firestore updateDoc blocked:", dbErr);
      }

      const updatedDocs = docsList.map(d => d.docId === docId ? { ...d, name: targetName } : d);
      setDocsList(updatedDocs);

      try {
        await createAuditLog('RENAME_DOCUMENT', selectedApp.applicationId, `Document renamed to ${targetName}`);
      } catch (dbErr) {
        console.warn("Audit log blocked:", dbErr);
      }
      setEditingDocId(null);
      setEditingDocName('');
    } catch (err) {
      console.error("Renaming error:", err);
    }
  };

  // 5. Create special document/file requests
  const handleCreateSpecialRequest = async () => {
    if (!selectedApp || !newRequestName.trim() || !ensureSelectedAppMutable()) return;

    const requestId = 'req_' + crypto.randomUUID();
    const req: SpecialRequest = {
      requestId,
      applicationId: selectedApp.applicationId,
      userId: selectedApp.userId,
      name: newRequestName,
      description: newRequestDesc,
      dueDate: newRequestDueDate || undefined,
      status: 'Pending'
    };

    try {
      const reqRef = doc(db, 'applications', selectedApp.applicationId, 'specialRequests', requestId);
      await setDoc(reqRef, req);

      const updated = [...specialRequests, req];
      setSpecialRequests(updated);

      await createAuditLog('CREATE_SPECIAL_REQUEST', selectedApp.applicationId, `Requested additional document: ${newRequestName}`);
      setNewRequestName('');
      setNewRequestDesc('');
      setNewRequestDueDate('');
      notifyToast('success', "Document request successfully sent to applicant.");
    } catch (err) {
      console.error("Failed creating special request:", err);
    }
  };

  // 5b. Create customizable Document Requests (top-level collection)
  const handleCreateDocumentRequest = async (): Promise<boolean> => {
    if (!selectedApp || !newRequestName.trim() || !ensureSelectedAppMutable()) return false;

    const userId = selectedApp.userId || (selectedApp as any).uid || (selectedApp as any).userUid || selectedApp.applicationId || '';
    if (!userId) {
      notifyToast('warning', "Application is missing a userId — cannot create document request");
      return false;
    }

    try {
      // Force token refresh so admin custom claim is current
      try {
        await ensureAdminClaimReady();
      } catch (tokenErr) {
        console.warn("Failed to refresh ID token:", tokenErr);
      }

      const token = await auth.currentUser?.getIdToken();

      const parsedRequiredDocs = (newRequestResponseType === 'file_upload' || newRequestResponseType === 'form') && newRequestRequiredDocs
        ? newRequestRequiredDocs.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const parsedDropdownOptions = (newRequestResponseType === 'dropdown' || (newRequestResponseType === 'form' && newRequestTextResponseType === 'dropdown')) && newRequestDropdownOptions
        ? newRequestDropdownOptions.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const parsedFormFields = newRequestResponseType === 'form'
        ? (Array.isArray(newRequestFormFields) ? newRequestFormFields : [])
        : [
            {
              id: `field_${Date.now()}`,
              label: newRequestName,
              type: newRequestResponseType,
              required: true,
              instructions: newRequestInstructions,
              options: parsedDropdownOptions.length > 0 ? parsedDropdownOptions : undefined
            }
          ];

      const response = await fetch('/api/admin/special-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newRequestName,
          description: newRequestDesc,
          instructions: newRequestInstructions,
          responseType: newRequestResponseType,
          priority: newReqDocPriority,
          dueDate: newRequestDueDate, // ISO date/time
          applicantId: selectedApp.applicationId,
          requiredDocs: parsedRequiredDocs,
          textResponseType: newRequestResponseType === 'form' ? newRequestTextResponseType : null,
          dropdownOptions: parsedDropdownOptions,
          formFields: parsedFormFields
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create Special Request via API');
      }

      const resData = await response.json();

      const newReq: any = {
        requestId: resData.requestId,
        applicantId: selectedApp.applicationId,
        userId,
        workspaceId: selectedApp.workspaceId,
        documentName: newRequestName,
        documentType: 'Special Request',
        responseType: newRequestResponseType,
        description: newRequestDesc,
        instructions: newRequestInstructions,
        dueDate: newRequestDueDate || undefined,
        priority: newReqDocPriority,
        status: 'Pending',
        token: resData.token,
        createdBy: auth.currentUser?.uid || 'admin',
        createdAt: new Date().toISOString(),
        requiredDocs: parsedRequiredDocs,
        textResponseType: newRequestResponseType === 'form' ? newRequestTextResponseType : null,
        dropdownOptions: parsedDropdownOptions,
        formFields: parsedFormFields
      };

      const updated = [...documentRequests, newReq];
      setDocumentRequests(updated);

      // Reset fields
      setNewRequestName('');
      setNewRequestDesc('');
      setNewRequestDueDate('');
      setNewRequestResponseType('short_text');
      setNewRequestInstructions('');
      setNewRequestRequiredDocs('');
      setNewRequestFormFields([]);
      setNewReqDocumentType('Required');
      setNewReqDocPriority('Medium');

      notifyToast('success', "Special Request successfully created and notification email sent to applicant.");
      return true;
    } catch (err: any) {
      console.error("Failed creating document request:", err);
      notifyToast('error', `Error: ${err.message}`);
      return false;
    }
  };

  // 5c. Edit / Save existing Document Request
  const handleUpdateDocumentRequest = async () => {
    if (!selectedApp || !editingRequestId || !editingRequestName.trim()) return;

    const currentReq = documentRequests.find(r => r.requestId === editingRequestId);
    if (currentReq && (currentReq.status === 'Submitted' || currentReq.status === 'Completed')) {
      notifyToast('warning', "This request has been completed/submitted by the applicant and cannot be edited.");
      setEditingRequestId(null);
      return;
    }

    try {
      const parsedRequiredDocs = (editingRequestResponseType === 'file_upload' || editingRequestResponseType === 'form') && editingRequestRequiredDocs
        ? editingRequestRequiredDocs.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const parsedDropdownOptions = (editingRequestResponseType === 'dropdown' || (editingRequestResponseType === 'form' && editingRequestTextResponseType === 'dropdown')) && editingRequestDropdownOptions
        ? editingRequestDropdownOptions.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const parsedFormFields = editingRequestResponseType === 'form'
        ? (Array.isArray(editingRequestFormFields) ? editingRequestFormFields : [])
        : [
            {
              id: `field_${Date.now()}`,
              label: editingRequestName,
              type: editingRequestResponseType,
              required: true,
              instructions: editingRequestInstructions,
              options: parsedDropdownOptions.length > 0 ? parsedDropdownOptions : undefined
            }
          ];

      const updatedFields: any = {
        documentName: editingRequestName,
        documentType: 'Special Request',
        responseType: editingRequestResponseType,
        description: editingRequestDesc,
        instructions: editingRequestInstructions,
        dueDate: editingRequestDueDate || null,
        priority: editingRequestPriority,
        requiredDocs: parsedRequiredDocs,
        textResponseType: editingRequestResponseType === 'form' ? editingRequestTextResponseType : null,
        dropdownOptions: parsedDropdownOptions,
        formFields: parsedFormFields
      };

      try {
        const reqRef = doc(db, 'documentRequests', editingRequestId);
        await updateDoc(reqRef, updatedFields);
      } catch (dbErr) {
        console.warn("Firestore update documentRequest blocked:", dbErr);
      }

      const updated = documentRequests.map(r => 
        r.requestId === editingRequestId 
          ? { ...r, ...updatedFields, dueDate: editingRequestDueDate || undefined, requiredDocs: parsedRequiredDocs }
          : r
      );
      setDocumentRequests(updated);

      try {
        await createAuditLog('UPDATE_DOCUMENT_REQUEST', selectedApp.applicationId, `Updated Special Request: ${editingRequestName}`);
      } catch (dbErr) {
        console.warn("Audit log blocked:", dbErr);
      }

      // Reset edit state
      setEditingRequestId(null);
      setEditingRequestRequiredDocs('');
    } catch (err) {
      console.error("Failed updating document request:", err);
    }
  };

  // 5d. Delete Customizable Document Request
  const handleDeleteDocumentRequest = async (requestId: string, docName: string) => {
    if (!selectedApp) return;
    if (!(await confirmDialog(`Are you sure you want to delete the document request for "${docName}"?`))) return;

    try {
      try {
        const reqRef = doc(db, 'documentRequests', requestId);
        await deleteDoc(reqRef);
      } catch (dbErr) {
        console.warn("Firestore delete documentRequest blocked:", dbErr);
      }

      const updated = documentRequests.filter(r => r.requestId !== requestId);
      setDocumentRequests(updated);

      try {
        await createAuditLog('DELETE_DOCUMENT_REQUEST', selectedApp.applicationId, `Deleted document request: ${docName}`);
      } catch (dbErr) {
        console.warn("Audit log blocked:", dbErr);
      }
    } catch (err) {
      console.error("Failed deleting document request:", err);
    }
  };

  // 5e. Mark document request status as completed/pending (Admin Manual override if needed)
  const handleSetDocRequestStatus = async (requestId: string, newStatus: string) => {
    if (!selectedApp) return;

    try {
      const updateData: any = { submissionStatus: newStatus, status: newStatus };
      if (newStatus === 'Completed') {
        updateData.uploadedAt = new Date().toISOString();
      }

      try {
        const reqRef = doc(db, 'documentRequests', requestId);
        await updateDoc(reqRef, updateData);
      } catch (dbErr) {
        console.warn("Firestore set status blocked:", dbErr);
      }

      const updated = documentRequests.map(r => 
        r.requestId === requestId 
          ? { ...r, ...updateData } 
          : r
      );
      setDocumentRequests(updated);

      try {
        await createAuditLog('REVIEW_DOCUMENT_REQUEST', selectedApp.applicationId, `Marked document request status as ${newStatus}`);
      } catch (dbErr) {
        console.warn("Audit log blocked:", dbErr);
      }
    } catch (err) {
      console.error("Failed updating document request status:", err);
    }
  };

  // 6. Comments & Internal Notes added by admin
  const handleCommentFileChange = (file: File | null) => {
    setCommentFile(file);
    if (!file) {
      setCommentFileUrl(null);
      setCommentFileName(null);
      setCommentFileProgress(null);
      return;
    }
    setCommentFileName(file.name);
  };

  const buildActivityFeed = useCallback((): ActivityEntry[] => {
    const entries: ActivityEntry[] = [];

    // Application created event
    if (selectedApp) {
      entries.push({
        id: 'app-created',
        type: 'application_created',
        description: 'Application created',
        timestamp: selectedApp.createdAt || selectedApp.updatedAt,
        actor: selectedApp.userEmail || 'Applicant',
      });
    }

    // Document uploads
    (docsList || []).forEach((doc: any) => {
      entries.push({
        id: `doc-${doc.docId}`,
        type: 'document_upload',
        description: `Uploaded: ${doc.fileName || doc.name || 'Document'}`,
        timestamp: doc.uploadedAt || doc.createdAt || new Date().toISOString(),
        actor: doc.uploadedBy || 'Applicant',
      });
    });

    // Comments
    (comments || []).forEach((c: Comment) => {
      entries.push({
        id: `comment-${c.commentId}`,
        type: 'comment',
        description: c.text || 'Comment added',
        timestamp: c.createdAt || new Date().toISOString(),
        actor: c.adminName || 'Admin',
      });
    });

    // Audit logs — group by bulkGroupId
    const bulkGroups: Record<string, AuditLog[]> = {};
    const standaloneLogs: AuditLog[] = [];
    (auditLogs || []).forEach((log: AuditLog) => {
      if (log.bulkGroupId) {
        if (!bulkGroups[log.bulkGroupId]) bulkGroups[log.bulkGroupId] = [];
        bulkGroups[log.bulkGroupId].push(log);
      } else {
        standaloneLogs.push(log);
      }
    });

    // Add grouped bulk action entries
    Object.values(bulkGroups).forEach((group, idx) => {
      const first = group[0];
      const actionLabel =
        first.action === 'BROADCAST_EMAIL' ? 'Bulk Email Campaign' :
        first.action === 'BULK_SIGN_REQUEST' ? 'Bulk Sign Request' :
        first.action === 'GROUP_EVENT_CREATED' ? 'Bulk Event' :
        first.action || 'Bulk Action';
      entries.push({
        id: `bulk-group-${idx}-${first.bulkGroupId}`,
        type: 'bulk_action',
        description: actionLabel,
        timestamp: [...group].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0].timestamp,
        actor: first.adminName || 'Admin',
        subEntries: group.map((log, i) => ({
          id: `bulk-sub-${idx}-${i}-${log.logId}`,
          type: 'status_change' as const,
          description: log.details || log.action || 'Bulk action',
          timestamp: log.timestamp,
          actor: log.adminName || 'Admin',
        })),
      });
    });

    // Add standalone audit log entries
    standaloneLogs.forEach((log: AuditLog) => {
      const desc = log.action || log.details || 'Status changed';
      entries.push({
        id: `audit-${log.logId}`,
        type: 'status_change',
        description: desc,
        timestamp: log.timestamp || new Date().toISOString(),
        actor: log.adminName || 'Admin',
      });
    });

    return entries;
  }, [selectedApp, docsList, comments, auditLogs]);

  const handleAddComment = async () => {
    if (!auth.currentUser) {
      showToast('Session expired. Please re-login.', 'error');
      return;
    }
    if (!selectedApp || (!newComment.trim() && !commentFile)) return;

    try {
      if (commentFile) {
        showToast('Comment attachments are temporarily unavailable while secure server-side uploads are migrated.', 'error');
        return;
      }
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ text: newComment.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const updatedComms = [...comments, data.comment as Comment];
      setComments(updatedComms);

      setNewComment('');
      setCommentFile(null);
      setCommentFileUrl(null);
      setCommentFileName(null);
      setCommentFileProgress(null);
      await createAuditLog('ADD_COMMENT', selectedApp.applicationId, `Added internal notebook note`);
    } catch (err) {
      console.error("Failed to add comment:", err);
      notifyToast('error', "Failed to post note: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsUploadingCommentFile(false);
      setCommentFileProgress(null);
    }
  };

  const handleEditComment = async (commentId: string, nextText: string) => {
    if (!selectedApp || !auth.currentUser || !nextText.trim()) return;
    const existing = comments.find(c => c.commentId === commentId);
    if (!existing) return;
    const now = new Date().toISOString();
    const updatedComment: Comment = {
      ...existing,
      text: nextText.trim(),
      updatedAt: now,
      edited: true,
      modifiedBy: auth.currentUser.uid,
    };
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() }, body: JSON.stringify({ text: updatedComment.text }),
    });
    const data = await res.json();
    if (!res.ok) { showToast(data?.error || 'Failed to update comment.', 'error'); return; }
    const updatedComments = comments.map(c => c.commentId === commentId ? updatedComment : c);
    setComments(updatedComments);
    setEditingCommentId(null);
    setEditingCommentText('');
    await createAuditLog('MODIFY_COMMENT', selectedApp.applicationId, `Comment ${commentId} was modified`);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedApp || !auth.currentUser) return;
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
    });
    const data = await res.json();
    if (!res.ok) { showToast(data?.error || 'Failed to delete comment.', 'error'); return; }
    setComments(comments.filter(c => c.commentId !== commentId));
    await createAuditLog('DELETE_COMMENT', selectedApp.applicationId, `Comment ${commentId} was deleted`);
  };


  const handleAddReply = async (parentCommentId: string) => {
    const parentComment = comments.find(c => c.commentId === parentCommentId);
    if (!parentComment || !selectedApp) return;

    const replyText = replyInputs[parentCommentId] || '';
    if (!replyText.trim()) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/comments/${encodeURIComponent(parentCommentId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() }, body: JSON.stringify({ replyText: replyText.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      // Update local state
      const updatedComments = comments.map(c => {
        if (c.commentId === parentCommentId) {
          return data.comment as Comment;
        }
        return c;
      });
      setComments(updatedComments);

      // Reset reply inputs
      setReplyInputs(prev => ({ ...prev, [parentCommentId]: '' }));
      setOpenReplyId(null);
      await createAuditLog('REPLY_COMMENT', selectedApp.applicationId, `Replied to comment ${parentCommentId}`);
    } catch (err) {
      console.error("Reply post error:", err);
    }
  };

  const handleEditReply = async (parentCommentId: string, replyId: string, nextText: string) => {
    if (!selectedApp || !auth.currentUser || !nextText.trim()) return;
    const parentComment = comments.find(c => c.commentId === parentCommentId);
    if (!parentComment) return;
    const existingReply = (parentComment.replies || []).find(r => r.replyId === replyId);
    if (!existingReply) return;
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`/api/admin/applications/${encodeURIComponent(selectedApp.applicationId)}/comments/${encodeURIComponent(parentCommentId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
      body: JSON.stringify({ replyId, replyText: nextText.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { showToast(data?.error || 'Failed to update reply.', 'error'); return; }
    setComments(comments.map(c => c.commentId === parentCommentId ? data.comment as Comment : c));
    setEditingReplyId(null);
    setEditingReplyText('');
    await createAuditLog('MODIFY_REPLY', selectedApp.applicationId, `Reply ${replyId} was modified`);
  };

  // 7. Profile Photo Uploader with resumable upload
  const handlePhotoUploadReal = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedApp) return;
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const compressedFile = await compressImage(file);

      const photoRef = ref(storage, `users/${selectedApp.userId}/${Date.now()}_${file.name}`);
      const snap = await uploadBytesWithTimeout(photoRef, compressedFile, 60000, 3);
      const downloadUrl = snap.downloadUrl;

      // Use server API to bypass Firestore permission issues
      await safeFetch(`/api/admin/applications/${selectedApp.applicationId}/update-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePhotoUrl: downloadUrl }),
      });

      const updatedApp = { ...selectedApp, profilePhotoUrl: downloadUrl, updatedAt: new Date().toISOString() };
      setSelectedApp(updatedApp);
      setProfilePhotoUrl(downloadUrl);
      setApplications(applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a));
      clearApplicationsCache();

      await createAuditLog('UPLOAD_PHOTO', selectedApp.applicationId, 'Admin uploaded/replaced candidate profile photo successfully.');
      showToast('Profile photo updated successfully!');
    } catch (photoErr: any) {
      console.error("Photo upload error:", photoErr);
      showToast(photoErr.message || getStorageErrorMessage(photoErr), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 7b. Save Assignment Details
  const handleSaveAssignment = async () => {
    if (!selectedApp) return;
    try {
      setLoading(true);
      const updatedAssignment = {
        employerName: editAssignmentEmployer,
        roleTitle: editAssignmentRole,
        city: editAssignmentCity,
        state: editAssignmentState,
        country: editAssignmentCountry
      };
      
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { assignment: updatedAssignment });
      
      const updatedApp = {
        ...selectedApp,
        assignment: updatedAssignment,
        updatedAt: new Date().toISOString()
      };
      
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a);
      setApplications(updatedList);
      clearApplicationsCache();
      setSelectedApp(updatedApp);
      setIsEditingAssignment(false);
      
      await createAuditLog('EDIT_ASSIGNMENT', selectedApp.applicationId, `Admin updated host job placement detailing: ${editAssignmentEmployer}`);

      // P-1: Notify applicant when host employer assigned
      const prevEmployer = selectedApp.assignment?.employerName;
      if (editAssignmentEmployer && editAssignmentEmployer !== prevEmployer) {
        const { emailPlacementAssigned } = await import('../../../lib/emailTriggers');
        await emailPlacementAssigned(selectedApp, editAssignmentEmployer);
      }

      // P-3: Notify applicant when role updated
      const prevRole = selectedApp.assignment?.roleTitle;
      if (editAssignmentRole && editAssignmentRole !== prevRole) {
        const { emailPlacementRoleUpdated } = await import('../../../lib/emailTriggers');
        await emailPlacementRoleUpdated(selectedApp, editAssignmentRole);
      }

      notifyToast('success', "Placement details stored successfully.");
    } catch (e) {
      console.error("Save assignment error:", e);
      notifyToast('error', "Failed storing placement details.");
    } finally {
      setLoading(false);
    }
  };

  // 7c. Save Visa stage details
  const handleSaveVisaDetails = async () => {
    if (!selectedApp) return;
    try {
      setLoading(true);
      const updatedVisa = {
        visaApplicationNumber: editVisaNo,
        visaApprovalStatus: editVisaStatus,
        embassyAppointmentDate: editVisaDate,
        embassyLocation: editVisaLocation
      };
      
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { visaDetails: updatedVisa });
      
      const updatedApp = {
        ...selectedApp,
        visaDetails: updatedVisa,
        updatedAt: new Date().toISOString()
      };
      
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a);
      setApplications(updatedList);
      clearApplicationsCache();
      setSelectedApp(updatedApp);
      setIsEditingVisa(false);
      
      await createAuditLog('EDIT_VISA_STAGE', selectedApp.applicationId, `Admin updated J1 embassy visa parameters. Status: ${editVisaStatus}`);
      notifyToast('success', "Visa specifications stored successfully.");
    } catch (e) {
      console.error("Save visa error:", e);
      notifyToast('error', "Failed storing visa specifications.");
    } finally {
      setLoading(false);
    }
  };

  // 7c2. Save Embassy Appointment details
  const handleSaveEmbassyDetails = async () => {
    if (!selectedApp) return;
    try {
      setLoading(true);
      const updatedEmbassy = {
        embassyAppointmentDate: editEmbassyDate,
        appointmentDate: editEmbassyDate, // preferred field used by email templates & batch center
        embassyLocation: editEmbassyLocation,
        interviewResult: editEmbassyResult,
      };

      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { embassyDetails: updatedEmbassy });

      const updatedApp = { ...selectedApp, embassyDetails: updatedEmbassy, updatedAt: new Date().toISOString() };
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a);
      setApplications(updatedList);
      clearApplicationsCache();
      setSelectedApp(updatedApp);
      setIsEditingEmbassy(false);

      await createAuditLog('EDIT_EMBASSY_DETAILS', selectedApp.applicationId, `Admin updated embassy appointment details. Result: ${editEmbassyResult}`);
      notifyToast('success', "Embassy appointment details saved successfully.");
    } catch (e) {
      console.error("Save embassy error:", e);
      notifyToast('error', "Failed storing embassy appointment details.");
    } finally {
      setLoading(false);
    }
  };

  // 7c3. Save DS-160 information
  const handleSaveDs160Details = async () => {
    if (!selectedApp) return;
    try {
      setLoading(true);
      const updatedDs160 = {
        ds160Number: editDs160No,
        ds160ConfirmationNumber: editDs160No,
        // Wave 4 (D-05): DS-160 security Q&A is no longer collected or saved.
        ds160SubmissionStatus: editDs160Status,
      };

      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { ds160Details: updatedDs160 });

      const updatedApp = { ...selectedApp, ds160Details: updatedDs160, updatedAt: new Date().toISOString() };
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a);
      setApplications(updatedList);
      clearApplicationsCache();
      setSelectedApp(updatedApp);
      setIsEditingDs160(false);

      await createAuditLog('EDIT_DS160_DETAILS', selectedApp.applicationId, `Admin updated DS-160 information. Status: ${editDs160Status}`);
      notifyToast('success', "DS-160 information saved successfully.");
    } catch (e) {
      console.error("Save DS-160 error:", e);
      notifyToast('error', "Failed storing DS-160 information.");
    } finally {
      setLoading(false);
    }
  };

  // 7d. Save Flight specifications
  const handleSaveFlightDetails = async () => {
    if (!selectedApp) return;
    try {
      setLoading(true);
      const updatedFlight = {
        airline: editFlightAirline,
        flightNumber: editFlightNo,
        departureDateTime: editFlightDepDateTime,
        arrivalDateTime: editFlightArrDateTime,
        departureAirport: editFlightDepAirport,
        arrivalAirport: editFlightArrAirport,
        additionalNotes: editFlightNotes
      };
      
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { flightDetails: updatedFlight });
      
      const updatedApp = {
        ...selectedApp,
        flightDetails: updatedFlight,
        updatedAt: new Date().toISOString()
      };
      
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updatedApp : a);
      setApplications(updatedList);
      clearApplicationsCache();
      setSelectedApp(updatedApp);
      setIsEditingFlight(false);
      
      await createAuditLog('EDIT_FLIGHT_DETAILS', selectedApp.applicationId, `Admin scheduled flights/shuttle coordination. Airline: ${editFlightAirline}`);
      notifyToast('success', "Flight specifications stored successfully.");
    } catch (e) {
      console.error("Save flight error:", e);
      notifyToast('error', "Failed storing flight specifications.");
    } finally {
      setLoading(false);
    }
  };

  // 7e. Quick Applicant Creation Action
  const handleUpdatePlacementStatus = async (status: 'Pending' | 'Confirmed') => {
    if (!selectedApp || !ensureSelectedAppMutable()) return;
    try {
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await adminProfileUpdate(selectedApp.applicationId, { placementStatus: status });
      const updated = { ...selectedApp, placementStatus: status, updatedAt: new Date().toISOString() };
      setSelectedApp(updated);
      const updatedList = applications.map(a => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList);
      clearApplicationsCache();
      await createAuditLog('UPDATE_PLACEMENT_STATUS', selectedApp.applicationId, `Placement status set to ${status}`);
      notifyToast('success', `Placement status updated to ${status}.`);

      // Trigger 3: Prompt to notify applicant of placement status update
      const appAny = selectedApp as any;
      const applicantName = firstUsablePersonName([appAny], '') || appAny.userEmail || 'Applicant';
      const applicantEmail = appAny.userEmail || appAny.personalDetails?.email || '';
      if (applicantEmail && (await confirmDialog(`Notify "${applicantName}" that their placement status is now "${status}"?`))) {
        const isConfirmed = status === 'Confirmed';
        const bodyHtml = isConfirmed
          ? `<p>We are pleased to inform you that your placement has been <strong style="color:#16a34a;">confirmed</strong>.</p><p>Our team will be in touch with next steps, including onboarding details and program preparation. If you have any questions in the meantime, please reach out to your program coordinator through the portal.</p>`
          : `<p>Your placement status has been updated to <strong>Pending</strong>.</p><p>We will notify you as soon as there are updates on your placement. Please continue to check your portal regularly for any changes or new information.</p>`;
        await sendSystemNotification({
          to: applicantEmail,
          toName: applicantName,
          subject: isConfirmed ? 'Placement Confirmed' : 'Placement Status Update – Pending',
          htmlBody: buildEmailTemplate({
            applicantName,
            title: isConfirmed ? 'Placement Confirmed' : 'Placement Status Update',
            bodyHtml,
            portalUrl: (window.__FRONTEND_URL || 'https://portal.isats.org') + '/applicant',
            ctaText: 'View My Dashboard',
          }),
          notificationType: isConfirmed ? 'placement_confirmed' : 'placement_pending',
          applicationId: selectedApp.applicationId,
        });
      }
    } catch (err) {
      console.error("Failed to update placement status:", err);
      notifyToast('error', "Failed to update placement status.");
    }
  };

  const handleUpdateTalentPoolEnabled = async (enabled: boolean) => {
    if (!selectedApp) return;
    try {
      const res = await fetch(`/api/admin/applications/${selectedApp.applicationId}/talent-pool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await auth.currentUser?.getIdToken()}` },
        body: JSON.stringify({ talentPoolEnabled: enabled }),
      });
      if (!res.ok) throw new Error('Failed to update');
      const updated = { ...selectedApp, talentPoolEnabled: enabled };
      setSelectedApp(updated as any);
      const updatedList = applications.map((a: any) => a.applicationId === selectedApp.applicationId ? updated : a);
      setApplications(updatedList as any);
      clearApplicationsCache();
      notifyToast('success', enabled ? 'Talent pool visibility enabled.' : 'Talent pool visibility disabled.');
    } catch (err) {
      console.error('Failed to update talent pool:', err);
      notifyToast('error', 'Failed to update talent pool visibility.');
    }
  };

  const handleCreateNewCandidate = async (
    e: React.FormEvent,
    wizardTerm?: string,
    wizardFirstName?: string,
    wizardLastName?: string,
    wizardEmail?: string,
    wizardProgramId?: string,
  ) => {
    e.preventDefault();
    // Accept direct wizard values to work around React state batching.
    // When called from the wizard, the setCandidate* updates haven't flushed
    // by the time we read candidateFirstName/LastName/Email from state.
    const firstName = (wizardFirstName || candidateFirstName || '').trim();
    const lastName = (wizardLastName || candidateLastName || '').trim();
    const email = (wizardEmail || candidateEmail || '').trim();
    if (!firstName || !lastName || !email) {
      notifyToast('warning', "Please fill out first name, last name, and email.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notifyToast('warning', "Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const targetProgram = (wizardProgramId || candidateProgram || '').trim();
      const progList = (typeof programsList !== 'undefined' ? programsList : []) as any[];
      const selectedProgramObj = progList.find((p: any) => p.id === targetProgram || p.programId === targetProgram);
      const progName = selectedProgramObj ? selectedProgramObj.name : targetProgram;
      const progId = selectedProgramObj ? (selectedProgramObj.programId || selectedProgramObj.id) : targetProgram;

      // Use wizardTerm directly if provided (avoids React state batching timing issues)
      const resolvedTerm = (wizardTerm || candidateProgramTerm || '').trim();

      // PT8-002: Call server endpoint — creates real Firebase Auth UID, preventing orphaned applications
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/create-candidate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.toLowerCase(),
          programId: progId,
          programName: progName,
          programTerm: resolvedTerm,
          sendWelcomeEmail,
          appId: undefined, // server will generate
          requirementsConfirmed: candidateRequirementsConfirmed,
          resumeUploaded: candidateResumeUploaded,
          resumeParsed: candidateResumeParsed,
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        notifyToast('error', 'Failed to create candidate: ' + err);
        return;
      }
      const data = await res.json();

      // Re-fetch applications list to include the new candidate
      const newAppId = data.applicationId;
      // Bust cache so fetchApplications hits Firestore
      clearApplicationsCache();
      await fetchApplications();
      await createAuditLog('CREATE_CANDIDATE', newAppId, `Quick created candidate profile ${firstName} ${lastName}`);

      // Send welcome email if checkbox was checked
      if (sendWelcomeEmail) {
        const brand = (await import('../../../lib/branding')).brand;
        const progDisplay = selectedProgramObj?.name || candidateProgram || '—';
        const welcomeHtml = `
          <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 560px; margin: 0 auto; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08);">
            <div style="text-align: center; padding: 36px 32px 8px 32px;">
              <span style="font-size: 28px; font-weight: 800; color: ${brand.colors.primaryDark}; letter-spacing: 2px;">${brand.businessName}</span>
            </div>
            <div style="text-align: center; padding: 8px 32px 4px 32px;">
              <h1 style="color: ${brand.colors.primaryDark}; font-size: 20px; font-weight: 700; margin: 0; padding: 0 0 16px 0; border-bottom: 2px solid #e8edf2;">Application Created</h1>
            </div>
            <div style="padding: 24px 32px 32px 32px;">
              <p style="font-size: 14px; color: #334155; line-height: 1.7; margin: 10px 0;">Dear ${firstName},</p>
              <p style="font-size: 14px; color: #334155; line-height: 1.7; margin: 10px 0;">
                Your application has been successfully created. We are pleased to welcome you to the program and support you as you take the next step toward your travel experience.
              </p>
              <table style="width: 100%; border-collapse: collapse; margin: 18px 0; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
                <tr><td style="padding: 10px 16px; font-size: 13px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b; width: 40%;">Application ID</td><td style="padding: 10px 16px; font-size: 13px; border-bottom: 1px solid #e2e8f0; color: #475569;">${newAppId}</td></tr>
                <tr><td style="padding: 10px 16px; font-size: 13px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b;">Email</td><td style="padding: 10px 16px; font-size: 13px; border-bottom: 1px solid #e2e8f0; color: #475569;">${email.toLowerCase()}</td></tr>
                <tr><td style="padding: 10px 16px; font-size: 13px; border-bottom: none; font-weight: 600; color: #1e293b;">Program</td><td style="padding: 10px 16px; font-size: 13px; border-bottom: none; color: #475569;">${progDisplay}</td></tr>
              </table>
              <p style="font-size: 14px; color: #334155; line-height: 1.7; margin: 10px 0;">
                To continue, please log in to your portal and complete your profile. This is a required step that unlocks your full application and allows us to prepare your program experience. Once your profile is completed, you will be able to view your application, including program details, placement updates, and important program information tailored to you.
              </p>
              <div style="text-align: center; margin: 28px 0 8px 0;">
                <a href="${window.location.origin}" style="display: inline-block; background: ${brand.colors.primaryDark}; color: #ffffff; text-decoration: none; padding: 12px 36px; border-radius: 6px; font-weight: 600; font-size: 14px;">Log In to Your Portal</a>
              </div>
            </div>
            <div style="text-align: center; padding: 24px 32px; border-top: 1px solid #e2e8f0;">
              <p style="font-size: 12px; color: #94a3b8; margin: 4px 0; line-height: 1.5;">Regards,</p>
              <p style="font-size: 12px; color: #94a3b8; margin: 4px 0; line-height: 1.5;">The ${brand.businessName} Team</p>
              <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e8edf2;">
                <p style="font-size: 12px; color: #94a3b8; margin: 4px 0;">&copy; ${new Date().getFullYear()} ${brand.businessName}. All rights reserved.</p>
              </div>
            </div>
          </div>
        `;

        try {
          const idToken = await auth.currentUser?.getIdToken();
          if (!idToken) { console.warn('Welcome email send failed: not authenticated'); return; }
          const sendRes = await fetch('/api/email/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({
              to: email.toLowerCase(),
              toName: `${firstName} ${lastName}`,
              subject: 'Welcome Aboard – Your Travel Journey Starts Here',
              htmlBody: welcomeHtml,
              applicationId: newAppId,
              sentBy: adminEmail,
            }),
          });
          if (!sendRes.ok) {
            const errBody = await sendRes.json().catch(() => ({}));
            console.warn('Welcome email send failed:', errBody.error || sendRes.status);
          }
        } catch (emailErr) {
          console.warn('Welcome email send failed (non-blocking):', emailErr);
        }
      }

      notifyToast('success', `Candidate associated under unique 8-digit sequential ID: ${newAppId}!`);
      
      // Reset State
      setCandidateFirstName('');
      setCandidateLastName('');
      setCandidateEmail('');
      setCandidateProgramTerm('');
      setSendWelcomeEmail(false);
      setIsCreatingCandidate(false);

      // Refresh applications list
      await fetchApplications();
    } catch (createErr) {
      console.error("Failed executing quick candidate creation:", createErr);
      notifyToast('error', "Error quick creating candidate record.");
    } finally {
      setLoading(false);
    }
  };

  // 8. ZIP DOWNLOAD SYSTEM - Calls Express server endpoint `/api/admin/zip-export`
  const handleDownloadAllAsZip = async (app: Application, filter?: 'all' | 'approved' | 'rejected') => {
    // Determine subset
    let filteredDocs = [...docsList];
    if (filter === 'approved') filteredDocs = docsList.filter(d => d.status === 'Approved');
    else if (filter === 'rejected') filteredDocs = docsList.filter(d => d.status === 'Rejected');

    if (filteredDocs.length === 0) {
      notifyToast('warning', "No documents match the selected filter for packaging.");
      return;
    }

    const namePart = firstUsablePersonName([app], '') || 'Applicant';
    const applicantFullNameWithId = `${namePart}(${app.applicationId || 'Unknown'})`;

    const packageItems = filteredDocs.map(doc => ({
      name: `${doc.type}_${doc.name}`,
      url: doc.downloadUrl || doc.storagePath
    }));

    // Add successfully uploaded special requests if any
    specialRequests.forEach(req => {
      if (req.uploadedStoragePath) {
        packageItems.push({
          name: `SpecialRequest_${req.name}.pdf`,
          url: req.uploadedStoragePath
        });
      }
    });

    console.log("Packaging items:", packageItems);

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/admin/zip-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          applicantName: applicantFullNameWithId,
          documents: packageItems
        })
      });

      if (!response.ok) {
        throw new Error(`Failed server-side zip generation: ${response.statusText}`);
      }

      // Convert chunk response payload to blob download
      const blob = await response.blob();
      const dowloadLink = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dowloadLink;
      a.download = `${applicantFullNameWithId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dowloadLink);

      const filterLabel = filter === 'approved' ? 'Approved' : filter === 'rejected' ? 'Rejected' : 'All';
      await createAuditLog('EXPORT_ZIP', app.applicationId, `Downloaded ${filterLabel} document archive ZIP (${filteredDocs.length} files)`);
    } catch (err) {
      console.error("Error creating ZIP download blob:", err);
      notifyToast('error', "Error packaging ZIP from Cloud Run backend. Confirm server connection.");
    }
  };

  // BULK ACTIONS
  const handleBulkStatusChange = async (targetStatus: ApplicationStatus) => {
    if (selectedAppIds.length === 0) return;
    if (!(await confirmDialog(`Are you sure you want to change the status of ${selectedAppIds.length} applicants to ${targetStatus}?`))) return;

    try {
      const now = new Date().toISOString();

      if (targetStatus === ApplicationStatus.SUBMITTED) {
        // S11: a bulk "Submit" must go through the server, never a raw
        // Firestore batch write. The direct SDK write bypassed every
        // server hook — no stage stamp, no submittedAt, no workflow
        // recompute — so bulk-submitted applicants never materialized
        // on the Workflow board. Route each id through the same admin
        // status endpoint handleUpdateStatus uses; its submission
        // transition stamps stage/submittedAt and materializes the
        // workflow record. Per-id failures are collected and surfaced
        // instead of silently flipping local state.
        const token = await auth.currentUser?.getIdToken();
        const succeededIds: string[] = [];
        const failed: string[] = [];
        for (const id of selectedAppIds) {
          try {
            const res = await fetch(`/api/admin/applications/${id}/status`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                status: ApplicationStatus.SUBMITTED,
                submissionStatus: ApplicationStatus.SUBMITTED,
                masterStage: 1,
              }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            succeededIds.push(id);
          } catch (bulkErr) {
            console.error(`Bulk submit failed for ${id}:`, bulkErr);
            failed.push(id);
          }
        }

        // Refresh local state for the ids the server actually updated
        const updatedList = applications.map(a => {
          if (succeededIds.includes(a.applicationId)) {
            return { ...a, submissionStatus: targetStatus, status: targetStatus, masterStage: 1, updatedAt: now };
          }
          return a;
        });
        setApplications(updatedList);
        calculateStats(updatedList);

        // Single audit log for the bulk action
        await createAuditLog('BULK_STATUS_CHANGE', 'batch', `Changed ${succeededIds.length} applicants to ${targetStatus}${failed.length ? ` (${failed.length} failed)` : ''}`);

        setSelectedAppIds([]);
        if (failed.length > 0) {
          notifyToast('warning', `Bulk submit completed with errors: ${succeededIds.length} succeeded, ${failed.length} failed. Failed application IDs: ${failed.join(', ')}`);
        } else {
          notifyToast('success', "Bulk status update completed successfully.");
        }
        return;
      }

      const batch = writeBatch(db);
      selectedAppIds.forEach(id => {
        batch.update(doc(db, 'applications', id), { submissionStatus: targetStatus, status: targetStatus, updatedAt: now });
      });
      await batch.commit(); // All updates in 1 round trip

      // Refresh local state
      const updatedList = applications.map(a => {
        if (selectedAppIds.includes(a.applicationId)) {
          return { ...a, submissionStatus: targetStatus, status: targetStatus, updatedAt: now };
        }
        return a;
      });
      setApplications(updatedList);
      calculateStats(updatedList);

      // Single audit log for the bulk action
      await createAuditLog('BULK_STATUS_CHANGE', 'batch', `Changed ${selectedAppIds.length} applicants to ${targetStatus}`);

      setSelectedAppIds([]);
      notifyToast('success', "Bulk status update completed successfully.");
    } catch (err) {
      console.error("Bulk status error:", err);
    }
  };

  const handleBulkProgramUpdate = async (newProgram: string) => {
    if (selectedAppIds.length === 0 || !newProgram) return;

    // Find the program object — the caller passes the program ID
    const progList = (typeof programsList !== 'undefined' ? programsList : []) as any[];
    const selected = progList.find((p: any) => p.id === newProgram || p.programId === newProgram);
    const programId = selected ? (selected.id || selected.programId) : newProgram;
    const programName = selected ? selected.name : newProgram;

    if (!(await confirmDialog(`Are you sure you want to change the program of ${selectedAppIds.length} applicants to "${programName}"?`))) return;

    try {
      await ensureAdminClaimReady();
      const batch = writeBatch(db);
      const now = new Date().toISOString();
      selectedAppIds.forEach(id => {
        // For each applicant, update programId/programType and merge programPreferences
        const app = applications.find(a => a.applicationId === id);
        const mergedPrefs = {
          ...(app?.programPreferences || {}),
          programType: programName,
        };
        batch.update(doc(db, 'applications', id), { programId, programType: programName, programPreferences: mergedPrefs, updatedAt: now });
      });
      await batch.commit();

      // Refresh local state
      const updatedList = applications.map(a => {
        if (selectedAppIds.includes(a.applicationId)) {
          const mergedPrefs = {
            ...(a.programPreferences || {}),
            programType: programName,
          };
          return { ...a, programId, programType: programName, programPreferences: mergedPrefs, updatedAt: now };
        }
        return a;
      });
      setApplications(updatedList as Application[]);
      calculateStats(updatedList as Application[]);
      await createAuditLog('BULK_PROGRAM_CHANGE', 'batch', `Changed ${selectedAppIds.length} applicants to program "${programName}"`);
      setSelectedAppIds([]);
      notifyToast('success', "Bulk program update completed successfully.");
    } catch (err) {
      console.error("Bulk program update error:", err);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedAppIds.length === 0) return;
    if (!(await confirmDialog(`Are you sure you want to archive ${selectedAppIds.length} applicant(s)?`))) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/applications/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ applicationIds: selectedAppIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Archive failed');
      setSelectedAppIds([]);
      clearApplicationsCache();
      await fetchApplications();
      notifyToast('success', `Successfully archived ${data.archivedCount} applicant(s).`);
    } catch (err: any) {
      console.error('Bulk archive error:', err.message);
      notifyToast('error', 'Archive failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAppIds.length === 0) return;
    if (!(await confirmDialog(`Are you sure you want to permanently delete ${selectedAppIds.length} applicant(s)? This cannot be undone.`))) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/applications/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ applicationIds: selectedAppIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setSelectedAppIds([]);
      clearApplicationsCache();
      await fetchApplications();
      notifyToast('success', `Successfully deleted ${data.deletedCount} applicant(s).`);
    } catch (err: any) {
      console.error('Bulk delete error:', err.message);
      notifyToast('error', 'Delete failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleBulkPartnerChange = async (providerId: string, targetApplicationIds?: string[]) => {
    const targetIds = (targetApplicationIds && targetApplicationIds.length > 0) ? targetApplicationIds : selectedAppIds;
    if (targetIds.length === 0 || !providerId) return;
    const provider = providers.find(p => p.providerId === providerId);
    if (!(await confirmDialog(`Are you sure you want to assign "${provider?.name || providerId}" to ${targetIds.length} applicant(s)?`))) return;

    try {
      const now = new Date().toISOString();
      await safeFetch('/api/admin/applications/bulk-partner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationIds: targetIds,
          providerId,
          serviceProvider: provider || providerId,
        }),
      });

      const updatedList = applications.map(a => {
        if (targetIds.includes(a.applicationId)) {
          return { ...a, assignedPartner: providerId, serviceProvider: provider || providerId, updatedAt: now };
        }
        return a;
      });
      setApplications(updatedList as Application[]);
      calculateStats(updatedList as Application[]);
      if (!targetApplicationIds) setSelectedAppIds([]);
      notifyToast('success', "Bulk partner update completed successfully.");
    } catch (err) {
      console.error("Bulk partner update error:", err);
      notifyToast('error', "Bulk partner update failed: " + ((err as any)?.message || String(err)));
    }
  };

  const handleBulkMasterStageChange = async (stageIndex: number) => {
    if (selectedAppIds.length === 0) return;
    if (!(await confirmDialog(`Are you sure you want to change the master phase of ${selectedAppIds.length} applicant(s) to "${MASTER_STAGES[stageIndex]?.label || stageIndex}"?`))) return;

    try {
      const now = new Date().toISOString();
      // Use server API to bypass Firestore permission issues
      await safeFetch('/api/admin/applications/bulk-master-stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: selectedAppIds, masterStage: stageIndex }),
      });

      const updatedList = applications.map(a => {
        if (selectedAppIds.includes(a.applicationId)) {
          return { ...a, masterStage: stageIndex, updatedAt: now };
        }
        return a;
      });
      setApplications(updatedList);
      calculateStats(updatedList);
      await createAuditLog('BULK_MASTER_STAGE_CHANGE', 'batch', `Changed master phase of ${selectedAppIds.length} applicants to stage ${stageIndex}`);
      setSelectedAppIds([]);
      notifyToast('success', "Bulk master phase update completed successfully.");
    } catch (err: any) {
      console.error("Bulk master phase update error:", err);
      showToast(err.message || 'Failed to update master phases', 'error');
    }
  };

  const handleBulkTagUpdate = async (tags: string[]) => {
    if (selectedAppIds.length === 0) return;
    if (!(await confirmDialog(`Are you sure you want to update tags for ${selectedAppIds.length} applicant(s)?`))) return;
    try {
      const batch = writeBatch(db);
      const now = new Date().toISOString();
      selectedAppIds.forEach(id => {
        batch.update(doc(db, 'applications', id), { tags, updatedAt: now });
      });
      await batch.commit();

      const updatedList = applications.map(a => {
        if (selectedAppIds.includes(a.applicationId)) {
          return { ...a, tags, updatedAt: now };
        }
        return a;
      });
      setApplications(updatedList);
      calculateStats(updatedList);
      const tagStr = tags.length > 0 ? tags.join(', ') : '(none)';
      await createAuditLog('BULK_TAG_UPDATE', 'batch', `Updated tags to "${tagStr}" for ${selectedAppIds.length} applicants`);
      setSelectedAppIds([]);
      notifyToast('success', "Bulk tag update completed successfully.");
    } catch (err) {
      console.error("Bulk tag update error:", err);
    }
  };

  // Filtering applicant list

  // ---- Computed values ----
  const filteredApps = (() => {
    // Globally filter active applications by default
    let list = (applications || []).filter(a => isApplicationActive(a));

    list = list.filter(a => filterApplicantRecord(a, unifiedFilters, {
      includeMasterPhase: false,
      programsList: (typeof programsList !== 'undefined' ? programsList : []) as any[],
    }));

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const progList = (typeof programsList !== 'undefined' ? programsList : []) as any[];
      list = list.filter(a => {
        const appAny = a as any;
        const matchedProg = progList.find((x: any) => x.id === a.programId || x.programId === a.programId || x.name === a.programType || x.code === a.programType);
        const progCode = (matchedProg?.code || appAny.programCode || '').toLowerCase();
        const progShortCode = (matchedProg?.shortCode || appAny.programShortCode || '').toLowerCase();

        const nameHit = [
          firstUsablePersonName([a], ''),
          sanitizeNamePart(appAny.applicantName),
          sanitizeNamePart(a.personalDetails?.fullNameLegal),
          sanitizeNamePart(appAny.fullName),
          sanitizeNamePart(appAny.name),
        ].some(n => n.toLowerCase().includes(term));
        return (
          nameHit ||
          a.userEmail?.toLowerCase().includes(term) ||
          a.applicationId?.toLowerCase().includes(term) ||
          appAny.id?.toLowerCase().includes(term) ||
          appAny.applicantId?.toLowerCase().includes(term) ||
          appAny.customApplicationId?.toLowerCase().includes(term) ||
          appAny.programName?.toLowerCase().includes(term) ||
          appAny.programType?.toLowerCase().includes(term) ||
          appAny.programId?.toLowerCase().includes(term) ||
          progCode.includes(term) ||
          progShortCode.includes(term)
        );
      });
    }

    return list;
  })();
  const totalPages = Math.ceil(filteredApps.length / itemsPerPage);
  const displayedApps = filteredApps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return {
    // State
    applications, setApplications,
    selectedApp, setSelectedApp,
    masterStage, setMasterStage,
    loading, setLoading,
    stats,
    searchTerm, setSearchTerm,
    statusFilter, setStatusFilter,
    placementFilter, setPlacementFilter,
    programFilter, setProgramFilter,
    tagFilter, setTagFilter,
    masterStageFilter, setMasterStageFilter,
    financialFilter, setFinancialFilter,
    genderFilter, setGenderFilter,
    countryFilter, setCountryFilter,
    partnerFilter, setPartnerFilter,
    batchFilter, setBatchFilter,
    termFilter, setTermFilter,
    agentFilter, setAgentFilter,
    workflowPhaseFilter, setWorkflowPhaseFilter,
    referrerFilter, setReferrerFilter,
    submittedDateFrom, setSubmittedDateFrom,
    submittedDateTo, setSubmittedDateTo,
    unifiedFilters, setUnifiedFilters,
    applicantViewMode, setApplicantViewMode,
    advFilters, setAdvFilters,
    selectedAppIds, setSelectedAppIds,
    applicantReturnOrigin, setApplicantReturnOrigin, viewApplicantInWorkspace,
    adminTab, setAdminTab,
    settingsInitialSubTab, setSettingsInitialSubTab,
    docsList, setDocsList,
    specialRequests, setSpecialRequests,
    documentRequests, setDocumentRequests,
    comments, setComments,
    auditLogs, setAuditLogs,
    authReady, setAuthReady,
    currentPage, setCurrentPage,
    itemsPerPage, setItemsPerPage,
    adminListViewMode, setAdminListViewMode,
    adminDetailTab, setAdminDetailTab,
    detailPrimaryTab, setDetailPrimaryTab,
    detailSecondaryTab, setDetailSecondaryTab,
    adminNoteInput, setAdminNoteInput,
    toast, setToast,
    showToast,
    isEditingPersonal, setIsEditingPersonal,
    editPersonal, setEditPersonal,
    // Document editing states
    editingDocId, setEditingDocId,
    editingDocName, setEditingDocName,
    // Comment states
    newComment, setNewComment,
    editingCommentId, setEditingCommentId,
    editingCommentText, setEditingCommentText,
    editingReplyId, setEditingReplyId,
    editingReplyText, setEditingReplyText,
    commentFile, setCommentFile,
    commentFileName, setCommentFileName,
    commentFileUrl, setCommentFileUrl,
    commentFileProgress, setCommentFileProgress,
    // Rejection modal
    rejectionModalDocId, setRejectionModalDocId,
    rejectionReasonInput, setRejectionReasonInput,
    isEditingAssignment, setIsEditingAssignment,
    editAssignmentEmployer, setEditAssignmentEmployer,
    editAssignmentRole, setEditAssignmentRole,
    editAssignmentCity, setEditAssignmentCity,
    editAssignmentState, setEditAssignmentState,
    editAssignmentCountry, setEditAssignmentCountry,
    isEditingVisa, setIsEditingVisa,
    editVisaNo, setEditVisaNo,
    editVisaStatus, setEditVisaStatus,
    editVisaDate, setEditVisaDate,
    editVisaLocation, setEditVisaLocation,
    isEditingEmbassy, setIsEditingEmbassy,
    editEmbassyDate, setEditEmbassyDate,
    editEmbassyLocation, setEditEmbassyLocation,
    editEmbassyResult, setEditEmbassyResult,
    handleSaveEmbassyDetails,
    isEditingDs160, setIsEditingDs160,
    editDs160No, setEditDs160No,
    editDs160QuestionAnswer, setEditDs160QuestionAnswer,
    editDs160Status, setEditDs160Status,
    handleSaveDs160Details,
    isEditingFlight, setIsEditingFlight,
    editFlightAirline, setEditFlightAirline,
    editFlightNo, setEditFlightNo,
    editFlightDepDateTime, setEditFlightDepDateTime,
    editFlightArrDateTime, setEditFlightArrDateTime,
    editFlightDepAirport, setEditFlightDepAirport,
    editFlightArrAirport, setEditFlightArrAirport,
    editFlightNotes, setEditFlightNotes,
    isEditingPassport, setIsEditingPassport,
    editPassport, setEditPassport,
    editPassportNo, setEditPassportNo,
    editPassportCountry, setEditPassportCountry,
    editPassportIssue, setEditPassportIssue,
    editPassportExpiry, setEditPassportExpiry,
    editPassVisaStatus, setEditPassVisaStatus,
    editDs2019, setEditDs2019,
    editSevisId, setEditSevisId,
    editIntendedArrival, setEditIntendedArrival,
    editUsDestination, setEditUsDestination,
    isEditingEducation, setIsEditingEducation,
    editEducation, setEditEducation,
    isEditingWork, setIsEditingWork,
    editWork, setEditWork,
    isEditingEmergency, setIsEditingEmergency,
    editEmergency, setEditEmergency,
    isEditingProgram, setIsEditingProgram,
    editProgram, setEditProgram,
    editPricingVariableId, setEditPricingVariableId,
    editProgramTerm, setEditProgramTerm,
    editProgIndustry, setEditProgIndustry,
    editProgState, setEditProgState,
    editProgStart, setEditProgStart,
    editProgDuration, setEditProgDuration,
    editProgEnglish, setEditProgEnglish,
    editProgSkills, setEditProgSkills,
    editProgNotes, setEditProgNotes,
    editProgRelocate, setEditProgRelocate,
    adminDocType, setAdminDocType,
    adminUploadedFile, setAdminUploadedFile,
    adminUploading, setAdminUploading,
    adminUploadProgress, setAdminUploadProgress,
    isCreatingCandidate, setIsCreatingCandidate,
    candidateFirstName, setCandidateFirstName,
    candidateLastName, setCandidateLastName,
    candidateEmail, setCandidateEmail,
    candidateProgram, setCandidateProgram,
    candidateProgramTerm, setCandidateProgramTerm,
    sendWelcomeEmail, setSendWelcomeEmail,
    resumeParsing, setResumeParsing, resumeParsed, setResumeParsed,
    candidateRequirementsConfirmed, setCandidateRequirementsConfirmed,
    candidateResumeUploaded, setCandidateResumeUploaded,
    candidateResumeParsed, setCandidateResumeParsed,
    isGeneratingProfileModalOpen, setIsGeneratingProfileModalOpen,
    // Preview doc
    previewDoc, setPreviewDoc,
    // Reply state
    replyInputs, setReplyInputs,
    openReplyId, setOpenReplyId,
    // Providers
    providers, setProviders,
    programsList, setProgramsList,
    programs: programsList, // alias — ApplicantDetail reads appCtx.programs
    adminEmail,
    // Document request states
    newRequestName, setNewRequestName,
    newRequestDesc, setNewRequestDesc,
    newRequestDueDate, setNewRequestDueDate,
    newReqDocumentType, setNewReqDocumentType,
    newReqDocPriority, setNewReqDocPriority,
    newRequestResponseType, setNewRequestResponseType,
    newRequestInstructions, setNewRequestInstructions,
    newRequestRequiredDocs, setNewRequestRequiredDocs,
    newRequestDropdownOptions, setNewRequestDropdownOptions,
    newRequestTextResponseType, setNewRequestTextResponseType,
    newRequestFormFields, setNewRequestFormFields,
    editingRequestId, setEditingRequestId,
    editingRequestName, setEditingRequestName,
    editingRequestType, setEditingRequestType,
    editingRequestDesc, setEditingRequestDesc,
    editingRequestDueDate, setEditingRequestDueDate,
    editingRequestPriority, setEditingRequestPriority,
    editingRequestResponseType, setEditingRequestResponseType,
    editingRequestInstructions, setEditingRequestInstructions,
    editingRequestRequiredDocs, setEditingRequestRequiredDocs,
    editingRequestDropdownOptions, setEditingRequestDropdownOptions,
    editingRequestTextResponseType, setEditingRequestTextResponseType,
    editingRequestFormFields, setEditingRequestFormFields,
    // Filters - computed
    filteredApps,
    displayedApps,
    totalPages,
    // Archive state & handlers
    selectedArchivedIds, setSelectedArchivedIds,
    showArchiveModal, setShowArchiveModal,
    archiving,
    handleArchiveSelected,
    // Handlers
    fetchApplications,
    getToken,
    ensureAdminClaimReady,
    adminProfileUpdate,
    piiProfileUpdate,
    fetchAuditLogs,
    fetchAndSeedPrograms,
    handleSelectApplicant,
    handleLazyLoadTab,
    handleMasterStageChange,
    handleToggleApplicationActive,
    handleReturnToApplicant,
    handleUpdateStatus,
    handleAssignProvider,
    handleUpdateAdminNotes,
    handleUpdateApplicantTags,
    handleSavePersonal,
    handleSavePassport,
    handleSaveEducation,
    handleSaveWork,
    handleSaveEmergency,
    handleSaveProgram,
    handleAdminUploadDocument,
    handleAdminReplaceDocument,
    handleAdminDeleteDocument,
    handleReviewDocument, reviewInFlightDocIds,
    handleRenameDocument,
    handleCreateSpecialRequest,
    handleCreateDocumentRequest,
    handleUpdateDocumentRequest,
    handleDeleteDocumentRequest,
    handleSetDocRequestStatus,
    handleCommentFileChange,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    handleAddReply,
    handleEditReply,
    handlePhotoUploadReal,
    handleBulkStatusChange,
    handleBulkProgramUpdate,
    handleBulkArchive,
    handleBulkDelete,
    handleBulkPartnerChange,
    handleBulkMasterStageChange,
    handleBulkTagUpdate,
    handleSaveAssignment,
    handleSaveVisaDetails,
    handleSaveFlightDetails,
    handleCreateNewCandidate,
    handleDownloadAllAsZip,
    handleUpdatePlacementStatus,
    handleUpdateTalentPoolEnabled,
    createAuditLog,
  };
}
